import { ocrSignal } from './ocr.signal';
import { camelCaseToTitleText } from 'utils/formatTxt';

/**
 * Same semantics as ApplicationDetailsTab `formatStoredPercentForDisplay`: stored value may be
 * a fraction (0–1) or percent points; show/edit as a percent number (e.g. 5.25).
 * @param {unknown} stored
 * @returns {string}
 */
export const formatPercentValueForDisplay = (stored) => {
  if (stored === null || stored === undefined || stored === '') return '';
  const num = Number(stored);
  if (Number.isNaN(num)) return String(stored);
  const pct = num > 0 && num <= 1 ? num * 100 : num;
  const rounded = Math.round(pct * 1e8) / 1e8;
  return String(rounded);
};

export const OCR_SELECT_OPTIONS = {
  requiredDocuments: [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
    { label: 'N/A', value: 'N/A' },
  ],
  demographicsRelatedData: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
    { label: 'Prefer not to respond', value: 'decline' },
  ],
  ethnicity: [
    { label: 'Hispanic or Latino', value: 'yes' },
    { label: 'Not Hispanic or Latino', value: 'no' },
    { label: 'Prefer not to respond', value: 'decline' },
  ],
  ancestry: [
    {
      label: 'Middle Eastern or North African',
      value: 'yes',
    },
    {
      label: 'Not Middle Eastern or North African',
      value: 'no',
    },
    { label: 'Prefer not to respond', value: 'decline' },
  ],
  gender: [
    { label: 'Female', value: 'female' },
    { label: 'Male', value: 'male' },
    { label: 'Nonbinary', value: 'nonBinary' },
    { label: 'Prefer not to respond', value: 'decline' },
  ],
  sexualOrientation: [
    { label: 'Gay or Lesbian', value: 'gayOrLesbian' },
    { label: 'Bisexual', value: 'bisexual' },
    { label: 'Straight', value: 'straight' },
    { label: 'Something else', value: 'somethingElse' },
    { label: 'Prefer not to respond', value: 'decline' },
  ],
  veteran: [
    { label: 'Veteran', value: 'yes' },
    { label: 'Non Veteran', value: 'no' },
    { label: 'Prefer not to respond', value: 'decline' },
  ],
  race: [
    { value: 'American Indian or Alaska Native', label: 'American Indian or Alaska Native', ocrKey: 'race_american_indian_or_alaskan_native' },
    { value: 'Asian', label: 'Asian', ocrKey: 'race_asian' },
    { value: 'Indian', label: 'Indian', ocrKey: 'race_indian' },
    { value: 'Chinese', label: 'Chinese', ocrKey: 'race_chinese' },
    { value: 'Filipino', label: 'Filipino', ocrKey: 'race_filipino' },
    { value: 'Japanese', label: 'Japanese', ocrKey: 'race_japanese' },
    { value: 'Korean', label: 'Korean', ocrKey: 'race_korean' },
    { value: 'Vietnamese', label: 'Vietnamese', ocrKey: 'race_vietnamese' },
    { value: 'Asian (Other)', label: 'Asian (Other)', ocrKey: 'race_asian_other' },
    { value: 'Black or African American', label: 'Black or African American', ocrKey: 'race_black_or_african_american' },
    { value: 'Native Hawaiian or Other Pacific Islander', label: 'Native Hawaiian or Other Pacific Islander', ocrKey: 'race_native_hawaiian_or_other_pacific_islander' },
    { value: 'Guamanian or Chamorro', label: 'Guamanian or Chamorro', ocrKey: 'race_guamanian_or_chamarro' },
    { value: 'Native Hawaiian', label: 'Native Hawaiian', ocrKey: 'race_native_hawaiian' },
    { value: 'Samoan', label: 'Samoan', ocrKey: 'race_samoan' },
    { value: 'Pacific Islander(Other)', label: 'Pacific Islander(Other)', ocrKey: 'race_pacific_islander' },
    { value: 'White', label: 'White', ocrKey: 'race_white' },
    { value: 'Prefer not to respond', label: 'Prefer not to respond', ocrKey: 'race_prefer_not_to_respond' },
  ],
  loaninformation_loan_type: [
    { label: 'Term Loan', value: 'T' },
    { label: 'Line of Credit', value: 'L' },
    { label: 'TRAC Lease', value: 'R' },
  ],
};


// This can be condensed once we retrain the OCR models and use more consistent naming
// Not labeled === CSP Programs
const OCR_DATA_TO_SELECT_INPUT = {
  // SSBCI Demographics
  ssbciDemographicsRelatedData_minorityOwnedBusinessYes: {
    fieldName: 'ssbciDemographics_minorityOwnedBusiness',
    value: 'yes',
  },
  ssbciDemographicsRelatedData_minorityOwnedBusinessNo: {
    fieldName: 'ssbciDemographics_minorityOwnedBusiness',
    value: 'no',
  },
  ssbciDemographicsRelatedData_minorityOwnedBusinessPreferNotToRespond: {
    fieldName: 'ssbciDemographics_minorityOwnedBusiness',
    value: 'decline',
  },
  ssbciDemographics_minorityOwnedOrControlledYes: {
    //sblgp
    fieldName: 'ssbciDemographics_minorityOwnedBusiness',
    value: 'yes',
  },
  ssbciDemographics_minorityOwnedOrControlledNo: {
    //sblgp
    fieldName: 'ssbciDemographics_minorityOwnedBusiness',
    value: 'no',
  },
  ssbciDemographics_minorityOwnedOrControlledNotRespond: {
    //sblgp
    fieldName: 'ssbciDemographics_minorityOwnedBusiness',
    value: 'decline',
  },
  ssbciDemographics_minorityOwnedBusinessYes: {
    // mlp
    fieldName: 'ssbciDemographics_minorityOwnedBusiness',
    value: 'yes',
  },
  ssbciDemographics_minorityOwnedBusinessNo: {
    // mlp
    fieldName: 'ssbciDemographics_minorityOwnedBusiness',
    value: 'no',
  },
  ssbciDemographics_minorityOwnedBusinessNA: {
    // mlp
    fieldName: 'ssbciDemographics_minorityOwnedBusiness',
    value: 'decline',
  },
  ssbciDemographicsRelatedData_womenOwnedBusinessYes: {
    fieldName: 'ssbciDemographics_womenOwnedBusiness',
    value: 'yes',
  },
  ssbciDemographicsRelatedData_womenOwnedBusinessNo: {
    fieldName: 'ssbciDemographics_womenOwnedBusiness',
    value: 'no',
  },
  ssbciDemographicsRelatedData_womenOwnedBusinessPreferNotToRespond: {
    fieldName: 'ssbciDemographics_womenOwnedBusiness',
    value: 'decline',
  },
  ssbciDemographics_womenOwnedOrControlledYes: {
    //sblgp
    fieldName: 'ssbciDemographics_womenOwnedBusiness',
    value: 'yes',
  },
  ssbciDemographics_womenOwnedOrControlledNo: {
    //sblgp
    fieldName: 'ssbciDemographics_womenOwnedBusiness',
    value: 'no',
  },
  ssbciDemographics_womenOwnedOrControlledNotRespond: {
    //sblgp
    fieldName: 'ssbciDemographics_womenOwnedBusiness',
    value: 'decline',
  },
  ssbciDemographics_WomenOwnedBusinessYes: {
    // mlp
    fieldName: 'ssbciDemographics_womenOwnedBusiness',
    value: 'yes',
  },
  ssbciDemographics_WomenOwnedBusinessNo: {
    // mlp
    fieldName: 'ssbciDemographics_womenOwnedBusiness',
    value: 'no',
  },
  ssbciDemographics_WomenOwnedBusinessNA: {
    // mlp
    fieldName: 'ssbciDemographics_womenOwnedBusiness',
    value: 'decline',
  },
  ssbciDemographicsRelatedData_veteranOwnedBusinessYes: {
    fieldName: 'ssbciDemographics_veteranOwnedBusiness',
    value: 'yes',
  },
  ssbciDemographicsRelatedData_veteranOwnedBusinessNo: {
    fieldName: 'ssbciDemographics_veteranOwnedBusiness',
    value: 'no',
  },
  ssbciDemographicsRelatedData_veteranOwnedBusinessPreferNotToRespond: {
    fieldName: 'ssbciDemographics_veteranOwnedBusiness',
    value: 'decline',
  },
  ssbciDemographics_veteranOwnedOrControlledYes: {
    //sblgp
    fieldName: 'ssbciDemographics_veteranOwnedBusiness',
    value: 'yes',
  },
  ssbciDemographics_veteranOwnedOrControlledNo: {
    //sblgp
    fieldName: 'ssbciDemographics_veteranOwnedBusiness',
    value: 'no',
  },
  ssbciDemographics_veteranOwnedOrControlledNotRespond: {
    //sblgp
    fieldName: 'ssbciDemographics_veteranOwnedBusiness',
    value: 'decline',
  },
  ssbciDemographics_veteranOwnedBusinessYes: {
    // mlp
    fieldName: 'ssbciDemographics_veteranOwnedBusiness',
    value: 'yes',
  },
  ssbciDemographics_veteranOwnedBusinessNo: {
    // mlp
    fieldName: 'ssbciDemographics_veteranOwnedBusiness',
    value: 'no',
  },
  ssbciDemographics_veteranOwnedBusinessNA: {
    // mlp
    fieldName: 'ssbciDemographics_veteranOwnedBusiness',
    value: 'decline',
  },
  // Ethnicity
  ethnicity_hispanicOrLatino: {
    fieldName: 'ethnicity_hispanicOrLatino',
    value: 'yes',
  },
  ethnicity_notHispanicOrLatino: {
    fieldName: 'ethnicity_hispanicOrLatino',
    value: 'no',
  },
  ethnicity_notRespond: {
    fieldName: 'ethnicity_hispanicOrLatino',
    value: 'decline',
  },
  ethnicity_preferNotToRespond: {
    //sblgp
    fieldName: 'ethnicity_hispanicOrLatino',
    value: 'decline',
  },
  // Ethnicity (snake_case from exampleFieldsFromOCr / document intelligence)
  ethnicity_hispanic_or_latino: {
    fieldName: 'ethnicity_hispanicOrLatino',
    value: 'yes',
  },
  ethnicity_not_hispanic_or_latino: {
    fieldName: 'ethnicity_hispanicOrLatino',
    value: 'no',
  },
  ethnicity_prefer_not_to_respond: {
    fieldName: 'ethnicity_hispanicOrLatino',
    value: 'decline',
  },
  ssbciDemographics_ethnicityHispanicLatino: {
    // mlp
    fieldName: 'ethnicity_hispanicOrLatino',
    value: 'yes',
  },
  ssbciDemographics_ethnicityNotHispanicLatino: {
    // mlp
    fieldName: 'ethnicity_hispanicOrLatino',
    value: 'no',
  },
  ssbciDemographics_ethnicityNotRespond: {
    // mlp
    fieldName: 'ethnicity_hispanicOrLatino',
    value: 'decline',
  },
  // Ancestry
  ancestry_middleEasternOrNorthAfrican: {
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'yes',
  },
  ancestry_notMiddleEasternOrNorthAfrican: {
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'no',
  },
  ancestry_preferNotToRespond: {
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'decline',
  },
  middleEastern_middleEasternOrnothAfrican: {
    //sblgp
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'yes',
  },
  middleEastern_notMiddleEasternNorth: {
    // sblgp
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'no',
  },
  middleEastern_preferNotRespond: {
    // sblgp
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'decline',
  },
  ssbciDemographics_ancestryMiddleEasterNorthAfrican: {
    // mlp
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'yes',
  },
  ssbciDemographics_ancestryNotMiddleEsternNorthAfrican: {
    // mlp
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'no',
  },
  ssbciDemographics_ancestryNotRespond: {
    // mlp
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'decline',
  },
  // Ancestry (snake_case from exampleFieldsFromOCr / document intelligence)
  ancestry_is_middle_eastern_or_northern_african: {
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'yes',
  },
  ancestry_is_not_middle_eastern_or_northern_african: {
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'no',
  },
  ancestry_prefer_not_to_respond: {
    fieldName: 'ancestry_middleEasternOrNorthAfrican',
    value: 'decline',
  },
  // Gender
  gender_female: {
    fieldName: 'gender_gender',
    value: 'female',
  },
  gender_male: {
    fieldName: 'gender_gender',
    value: 'male',
  },
  gender_nonbinary: {
    fieldName: 'gender_gender',
    value: 'nonBinary',
  },
  gender_preferNotToRespond: {
    fieldName: 'gender_gender',
    value: 'decline',
  },
  gender_preferNotRespond: {
    //sblgp
    fieldName: 'gender_gender',
    value: 'decline',
  },
  // Gender (snake_case from exampleFieldsFromOCr / document intelligence)
  gender_prefer_to_selfdescribe: {
    fieldName: 'gender_gender',
    value: 'decline',
  },
  ssbciDemographics_genderFemale: {
    // mlp
    fieldName: 'gender_gender',
    value: 'female',
  },
  ssbciDemographics_genderMale: {
    // mlp
    fieldName: 'gender_gender',
    value: 'male',
  },
  ssbciDemographics_genderNonBinary: {
    // mlp
    fieldName: 'gender_gender',
    value: 'nonBinary',
  },
  ssbciDemographics_genderNotRespond: {
    // mlp
    fieldName: 'gender_gender',
    value: 'decline',
  },
  // Sexual Orientation
  sexualOrientation_gayOrLesbian: {
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'gayOrLesbian',
  },
  sexualOrientation_bisexual: {
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'bisexual',
  },
  sexualOrientation_straight: {
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'straight',
  },
  sexualOrientation_somethingElse: {
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'somethingElse',
  },
  sexualOrientation_preferNotToRespond: {
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'decline',
  },
  sexualOrientation_gayLesbian: {
    // sblgp
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'gayOrLesbian',
  },
  sexualOrientation_straightNotGayLesbianBisexual: {
    // sblgp
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'straight',
  },
  sexualOrientation_preferNotRespond: {
    // sblgp
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'decline',
  },
  // Sexual Orientation (snake_case from exampleFieldsFromOCr / document intelligence)
  sexualorientation_gay_or_lesbian: {
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'gayOrLesbian',
  },
  sexualorientation_bisexual: {
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'bisexual',
  },
  sexualorientation_straight_that_is_not_gay_lesbian_or_bisexual: {
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'straight',
  },
  sexualorientation_something_else: {
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'somethingElse',
  },
  sexualorientation_prefer_not_to_respond: {
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'decline',
  },
  ssbciDemographics_sexOrientationGayLesbian: {
    // mlp
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'gayOrLesbian',
  },
  ssbciDemographics_sexOrientationBisexual: {
    // mlp
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'bisexual',
  },
  ssbciDemographics_sexOrientationStraight: {
    // mlp
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'straight',
  },
  ssbciDemographics_sexOrientationSomethingElse: {
    // mlp
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'somethingElse',
  },
  ssbciDemographics_sexOrientationNotRespond: {
    // mlp
    fieldName: 'sexualOrientation_sexualOrientation',
    value: 'decline',
  },

  // Veteran Status (snake_case from exampleFieldsFromOCr / document intelligence)
  veteran_status_veteran: {
    fieldName: 'veteranStatus_veteranStatus',
    value: 'yes',
  },
  veteran_status_non_veteran: {
    fieldName: 'veteranStatus_veteranStatus',
    value: 'no',
  },
  veteran_status_prefer_not_to_respond: {
    fieldName: 'veteranStatus_veteranStatus',
    value: 'decline',
  },
  loaninformation_loan_type: {
    fieldName: 'loaninformation_loan_type',
    value: 'T',
  },
};



const CALCAP_SB_CATEGORY_ORDER = [
  'lenderInformation',
  'lenderinformation',
  'generalInfo',
  'generalinfo',
  'generalnfo',
  'section1',
  'box1',
  'box2',
  'box3',
  'borrowerInformation',
  'borrowerinformation',
  'loanInformation',
  'loaninformation',
  'lenderCertification',
  'calcapLenderCertification',
  'loanCertification',
  'calcapUseOnly',
  'calcapuseonly',
  'borrowerCertificationForSmallBusinessLoanEnrollments',
  'calpapBorrowerAgreement',
  'calpapBorrowerCertification',
  'borrowerDemographics',
  'ccapSediOwnedBusiness',
  'supplumentalSaccertification',
  'calpapCertification',
];

/** Section order for cpcfa-calcap-csp (CalCAP CSP) when boundingRegions are not available. */
/** Demographics-related category prefixes merged into one "Borrower Demographics" section for CSP. */
const DEMOGRAPHICS_CATEGORY_PREFIXES = [
  'borrowerdemographics',
  'ethnicity',
  'ancestry',
  'gender',
  'sexualorientation',
  'veteran',
  'race',
];

const CALCAP_CSP_CATEGORY_ORDER = [
  'lenderinformation',
  'borrowerinformation',
  'box1',
  'box2',
  'box3',
  'collateral',
  'calcaplendercertification',
  'calcapborroweragreement',
  'calcapborrowercertification',
  'borrowerdemographics',
  'ccapsediownedbusiness',
  'supplementalsaccertification',
  'calcapuseonly',
  'calcapcertification',
];



const formatTableData = (data, fieldName) => {
  if (!data?.length) return;
  const first = data[0];
  const props = first?.properties ?? first;
  const headers = Object.keys(props ?? {});
  if (!headers.length) return;
  let rows = [];
  if (fieldName.includes('requiredDocuments')) {
    rows = data.map((d) => {
      const rowObj = { Provided: '', Documents: '' };
      const dProps = d?.properties ?? d;
      rowObj['Documents'] = dProps?.['Documents']?.value ?? dProps?.['Documents'] ?? '';
      headers.filter((h) => h !== 'Documents').forEach((header) => {
        const v = dProps?.[header]?.value ?? dProps?.[header];
        if (v) rowObj['Provided'] = header;
      });
      return rowObj;
    });
    return { headers: ['Documents', 'Provided'], rows };
  }
  rows = data.map((d) => {
    const rowObj = {};
    const dProps = d?.properties ?? d;
    headers.forEach((header) => {
      rowObj[header] = dProps?.[header]?.value ?? dProps?.[header] ?? '';
    });
    return rowObj;
  });
  return { headers, rows };
};

/** Converts snake_case to Title Case (e.g. "is_middle_eastern" -> "Is Middle Eastern") */
const snakeToTitle = (str) =>
  str
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

/** Format label for display - handles both camelCase (digital form) and snake_case (Document Intelligence) */
const formatFieldLabel = (str) =>
  str?.includes('_') ? snakeToTitle(str) : camelCaseToTitleText(str || '');

/**
 * Transforms a raw OCR field object into a form-ready field definition.
 * Applies OCR_DATA_TO_SELECT_INPUT mapping for demographics/select fields.
 * Supports both digital form API (value, content, kind) and Document Intelligence (valueString, etc).
 */
/** First non-empty value from digital form / Document Intelligence fields. */
const coalesceValue = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && v !== '');

/**
 * PDF Yes/No often arrives as: selection marks → boolean after formatOcrDataForSubmit,
 * or Document Intelligence valueString "Yes"/"No". Maps to app booleans (true/false).
 */
const ocrYes = (v) => {
  if (v === true || v === 'selected') return true;
  if (v === false || v == null || v === '') return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'no' || s === 'n' || s === 'false' || s === '0') return false;
    return s === 'yes' || s === 'y' || s === 'true';
  }
  return false;
};

/** Maps OCR loan type label or code (T/L/R) to select value T, L, or R. */
const normalizeLoanTypeOcrValue = (raw) => {
  if (raw == null || raw === '') return undefined;
  const s = String(raw).trim();
  const options = OCR_SELECT_OPTIONS.loaninformation_loan_type;
  const byLabel = new Map(options.map((o) => [o.label.toLowerCase(), o.value]));
  const lower = s.toLowerCase();
  if (byLabel.has(lower)) return byLabel.get(lower);
  if (options.some((o) => o.value === s)) return s;
  return s;
};

/**
 * Borrower Information PDF shows Yes/No; Azure DI often returns `type: string` + valueString "Yes"/"No"
 * instead of `selectionMark`. Keys vary: borrowerinformation_is_woman_owned, borrowerInformation_isWomanOwned,
 * borrower_information_is_woman_owned, etc. Match by normalizing underscores/case.
 */
const isBorrowerInformationYesNoCheckboxKey = (key) => {
  if (typeof key !== 'string' || !key.trim()) return false;
  const compact = key.toLowerCase().replace(/_/g, '');
  if (!compact.startsWith('borrowerinformation')) return false;
  const after = compact.slice('borrowerinformation'.length);
  return (
    after === 'iswomanowned' ||
    after === 'isminorityowned' ||
    after === 'isveteranowned' ||
    after === 'severelyaffected'
  );
};

/**
 * Document Intelligence selection marks expose `valueSelectionMark` ("selected" | "unselected").
 * `content` is often ":selected:" / ":unselected:" — if we coalesce content first, mapped selects
 * never see "selected" and OCR_DATA_TO_SELECT_INPUT stays empty (e.g. gender_male → gender_gender).
 */
const coalesceFieldValueForTransform = (rawField, rawType) => {
  if (rawType === 'selectionMark') {
    return coalesceValue(
      rawField.value,
      rawField.valueSelectionMark,
      rawField.valueString,
      rawField.content,
      rawField.valueDate,
      rawField.valueNumber,
      rawField.values
    );
  }
  return coalesceValue(
    rawField.value,
    rawField.content,
    rawField.valueString,
    rawField.valueSelectionMark,
    rawField.valueDate,
    rawField.valueNumber,
    rawField.values
  );
};

/** Field names that should use the percent input in OCR UI (must live before `transformFieldForForm`). */
const OCR_PERCENT_FIELD_NAMES = new Set([
  'calcapborroweragreement_borrower_percentage',
  'calcapborroweragreement_lender-percentage',
  'calcapborroweragreement_lender_percentage',
  'calcapborroweragreement_total_percentage_paid_to_calcap',
  'calcapborroweragreement_total_percentage_paid_by_borrower',
  'calcapuseonly_percentage_1',
  'calcapuseonly_percentage_2',
  'calcapuseonly_percentage_3',
  'loaninformation_borrower_percentage',
  'loaninformation_lender_percentage',
  'loaninformation_percentage_of_loan_enrolled_in_calcap',
  'loaninformation_loan_percentage_enrolled_in_another_program',
  'calcap_sb_csp_questionaire_sec1_percentage_of_building_occupied_by_business',
]);

/** True for keys in {@link OCR_PERCENT_FIELD_NAMES} (also use in OCRRender when saved `type` is missing). */
export const isOcrPercentFieldName = (fieldName) =>
  typeof fieldName === 'string' && OCR_PERCENT_FIELD_NAMES.has(fieldName);

const OCR_CURRENCY_FIELD_NAMES = new Set([
  'calcapborroweragreement_total_fees_paid_to_calcap',
  'calcapborroweragreement_total_fees_paid_by_borrower',
  'calcapborroweragreement_borrower_fee',
  'calcapborroweragreement_lender_fee',
  'calcapborroweragreement_total_amount_enrolled_in_calcap_or_advance_for_this_transaction',
  'calcapuseonly_contribution_amount_1',
  'calcapuseonly_contribution_amount_2',
  'calcapuseonly_contribution_amount_3',
]);

/** Supplemental SAC Certification (Gustavo Jul 23): keep section; OCR must not populate — blank on intake. */
export const isSupplementalSacCertificationField = (key) =>
  typeof key === 'string' &&
  key.toLowerCase().startsWith('supplementalsaccertification_');

const blankSupplementalSacFieldValue = (field) => {
  if (!field || typeof field !== 'object') return field;
  const key = field.key ?? field.name;
  if (isOcrAnalystSignatureConfirmField(key)) {
    return {
      ...field,
      type: 'select',
      options: OCR_SIGNATURE_CONFIRM_YES_NO_OPTIONS,
      value: '',
    };
  }
  if (field.type === 'checkbox') {
    return { ...field, value: false };
  }
  if (field.type === 'table') {
    const headers = field.value?.headers ?? [];
    const rows = (field.value?.rows ?? []).map((row) => {
      const empty = {};
      headers.forEach((h) => {
        empty[h] = '';
      });
      return empty;
    });
    return { ...field, value: { headers, rows } };
  }
  return { ...field, value: '' };
};

/** Blank all Supplemental SAC OCR fields after parse (section stays visible). */
export const blankSupplementalSacCertificationFields = (parsedOcrData) => {
  if (!parsedOcrData || typeof parsedOcrData !== 'object') return parsedOcrData;
  const out = { ...parsedOcrData };
  Object.keys(out).forEach((cat) => {
    if (String(cat).toLowerCase() !== 'supplementalsaccertification') return;
    if (!Array.isArray(out[cat])) return;
    out[cat] = out[cat].map(blankSupplementalSacFieldValue);
  });
  return out;
};

/**
 * Borrower + Authorized signature fields (Gustavo E2E / Jul 23):
 * Yes/No dropdown for analyst confirm — blank on intake, not OCR MISSING/RECEIVED.
 */
const OCR_SIGNATURE_CONFIRM_YES_NO_OPTIONS = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

export const isOcrAnalystSignatureConfirmField = (key) => {
  if (!key || typeof key !== 'string') return false;
  const k = key.toLowerCase();
  if (k.includes('date')) return false;
  if (
    k.includes('borrower_signature') ||
    k.includes('borrowers_signature') ||
    k.includes('signature_of_above_individual')
  ) {
    return true;
  }
  if (k.includes('authorized_lender_signature')) return true;
  // e.g. calcapuseonly_authorized_signature
  if (k.endsWith('authorized_signature') || k.includes('_authorized_signature')) {
    return true;
  }
  return false;
};

/** Keep Yes/No if analyst already set; blank legacy MISSING/RECEIVED and raw OCR blobs. */
export const normalizeSignatureConfirmSelectValue = (v) => {
  if (v == null || v === '') return null;
  const s = String(v).trim();
  const low = s.toLowerCase();
  if (low === 'yes' || low === 'y') return 'Yes';
  if (low === 'no' || low === 'n') return 'No';
  if (
    low === 'missing' ||
    low === 'received' ||
    low === 'signed' ||
    low === 'unsigned' ||
    low === 'true' ||
    low === 'false'
  ) {
    return null;
  }
  return null;
};

/** Patch saved/stale parsed OCR so signature confirm fields are Yes/No selects (blank until set). */
export const applyAnalystSignatureConfirmFields = (
  parsedOcrData,
  flatFormData = null
) => {
  if (!parsedOcrData || typeof parsedOcrData !== 'object') return parsedOcrData;
  const out = { ...parsedOcrData };
  Object.keys(out).forEach((cat) => {
    const fields = out[cat];
    if (!Array.isArray(fields)) return;
    out[cat] = fields.map((f) => {
      const key = f?.key ?? f?.name;
      if (!isOcrAnalystSignatureConfirmField(key)) return f;
      const rawValue =
        flatFormData && key != null && Object.prototype.hasOwnProperty.call(flatFormData, key)
          ? flatFormData[key]
          : f?.value;
      return {
        ...f,
        type: 'select',
        options: OCR_SIGNATURE_CONFIRM_YES_NO_OPTIONS,
        value: normalizeSignatureConfirmSelectValue(rawValue) ?? '',
      };
    });
  });
  return out;
};

const transformFieldForForm = (rawField) => {
  const key = rawField.key ?? rawField.name ?? rawField.fieldName;
  const rawType = rawField.type || rawField.kind;
  let value = coalesceFieldValueForTransform(rawField, rawType);

  const mapping = key ? OCR_DATA_TO_SELECT_INPUT[key] : undefined;
  if (mapping) {
    const selected = value === 'selected' || value === ':selected:';
    let mappedValue = selected ? mapping.value : undefined;
    let labelPart = mapping.fieldName.split('_').slice(0)
    let options = [];
    if (mapping.fieldName.includes('ethnicity')) {
      options = OCR_SELECT_OPTIONS.ethnicity;
      labelPart = 'Ethnicity';
    } else if (
      mapping.fieldName.includes('ancestry') ||
      mapping.fieldName.includes('middleEastern')
    ) {
      options = OCR_SELECT_OPTIONS.ancestry;
      labelPart = 'Ancestry';
    } else if (mapping.fieldName.includes('gender')) {
      options = OCR_SELECT_OPTIONS.gender;
      labelPart = 'Gender';
    } else if (mapping.fieldName.includes('sexualOrientation')) {
      options = OCR_SELECT_OPTIONS.sexualOrientation;
      labelPart = 'Sexual Orientation';
    } else if (mapping.fieldName.includes('veteran')) {
      options = OCR_SELECT_OPTIONS.veteran;
      labelPart = 'Veteran Status';
    } else if (mapping.fieldName.includes('ssbciDemographics')) {
      options = OCR_SELECT_OPTIONS.demographicsRelatedData;
      labelPart = 'Demographics Related Data';
    } else if (mapping.fieldName.includes('loaninformation_loan_type')) {
      options = OCR_SELECT_OPTIONS.loaninformation_loan_type;
      labelPart = 'Loan Type';
    }

    // Document Intelligence returns valueString (e.g. "Term Loan"), not selectionMark "selected".
    if (
      mapping.fieldName.includes('loaninformation_loan_type') &&
      mappedValue === undefined &&
      value != null &&
      value !== '' &&
      value !== 'selected'
    ) {
      mappedValue = normalizeLoanTypeOcrValue(value);
    }

    return {
      ...rawField,
      key,
      name: mapping.fieldName,
      label: labelPart,
      value: mappedValue,
      type: 'select',
      options,
    };
  }

  let type = 'text-input';
  if (rawType === 'selectionMark') {
    type = 'checkbox';
    value = value === 'selected' || value === ':selected:';
  } else if (rawType === 'array') {
    type = 'table';
    value = formatTableData(value, key);
  } else if (rawType === 'string' || rawType === 'number' || rawType === 'date') {
    type = 'text-input';
  }

  if (type !== 'table' && key && isBorrowerInformationYesNoCheckboxKey(key)) {
    type = 'checkbox';
    value = ocrYes(value);
  }

  if (type === 'text-input' && key && OCR_PERCENT_FIELD_NAMES.has(key)) {
    type = 'percent';
    if (value != null && value !== '') {
      const s = String(value).trim();
      const n = parseFloat(s.replace(/%/g, '').replace(/[^0-9.-]/g, ''));
      value = Number.isFinite(n) ? String(n) : s.replace(/%/g, '').trim();
    }
  }

  if (type === 'text-input' && key && OCR_CURRENCY_FIELD_NAMES.has(key)) {
    type = 'currency';
    if (value != null && value !== '') {
      const n = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
      value = Number.isFinite(n) ? n : 0;
    } else {
      value = 0;
    }
  }

  const parts = (key || '').split('_');
  const labelPart =
    parts.length > 1 ? parts.slice(1).join('_') : key;
  const label = formatFieldLabel(labelPart);

  let options;
  if (isOcrAnalystSignatureConfirmField(key)) {
    // Blank on intake — analyst confirms Yes/No that signatures are on the PDF.
    type = 'select';
    options = OCR_SIGNATURE_CONFIRM_YES_NO_OPTIONS;
    value = '';
  } else if (key && key.includes('signature')) {
    value = value?.length > 0 ? 'RECEIVED' : 'MISSING';
  }

  return {
    ...rawField,
    key,
    name: key,
    label,
    value,
    type,
    options,
  };
};

/**
 * Collapses select-mapped fields that share the same logical fieldName
 * (e.g. ancestry_is_yes, ancestry_no, ancestry_decline -> one ancestry select).
 */
const collapseSelectFields = (fields) => {
  const byName = new Map();
  for (const field of fields) {
    if (field.type === 'select') {
      const existing = byName.get(field.name);
      const merged = existing
        ? { ...existing, value: field.value ?? existing.value }
        : field;
      byName.set(field.name, merged);
    } else {
      byName.set(field.key, field);
    }
  }
  return Array.from(byName.values());
};

/** Display order for borrower Yes/No flags so OCRRender places them on one row (3× lg=4). */
const BORROWER_OWNERSHIP_GROUP_SUFFIXES = [
  'isminorityowned',
  'iswomanowned',
  'isveteranowned',
  'severelyaffected',
];

const compactOcrFieldKey = (key) =>
  typeof key === 'string' ? key.toLowerCase().replace(/_/g, '') : '';

const isBorrowerOwnershipGroupField = (field) => {
  const key = field.key ?? field.name;
  const c = compactOcrFieldKey(key);
  if (!c.startsWith('borrowerinformation')) return false;
  const after = c.slice('borrowerinformation'.length);
  return BORROWER_OWNERSHIP_GROUP_SUFFIXES.includes(after);
};

/** Same predicate; exported for OCRRender to lay out ownership checkboxes on one row. */
export const isBorrowerOwnershipOcrField = isBorrowerOwnershipGroupField;

/**
 * Split borrower category fields into single fields vs contiguous ownership checkbox runs
 * (render runs as a full-width inner row so they are never split across grid rows).
 */
export const chunkFieldsForBorrowerOwnershipRow = (fields) => {
  if (!Array.isArray(fields) || !fields.length) return [];
  const out = [];
  let i = 0;
  while (i < fields.length) {
    if (isBorrowerOwnershipOcrField(fields[i])) {
      const group = [];
      while (i < fields.length && isBorrowerOwnershipOcrField(fields[i])) {
        group.push(fields[i++]);
      }
      out.push({ kind: 'ownership', fields: group });
    } else {
      out.push({ kind: 'field', field: fields[i++] });
    }
  }
  return out;
};

/** Document order for Supplemental SAC Certification (printed signer before lender signature). */
const SUPPLEMENTAL_SAC_CERT_FIELD_ORDER = [
  'supplementalsaccertification_qualifying_event',
  'supplementalsaccertification_address_located_in_cdfi_investment_area',
  'supplementalsaccertification_printer_borrower_name',
  'supplementalsaccertification_borrower_date',
  'supplementalsaccertification_borrower_signature',
  'supplementalsaccertification_printed_name_authorized_signer',
  'supplementalsaccertification_title_authorized_signer',
  'supplementalsaccertification_authorized_signer_date',
  'supplementalsaccertification_authorized_lender_signature',
  'supplementalsaccertification_lender_loan_number',
];

/**
 * CalCAP SEDI-Owned Business bottom block: row 1 legal/loan/date, row 2 CDFI address + borrower print/sign,
 * row 3 authorized signer + title + lender date, then remaining fields.
 */
const CCAPSEDI_NON_CHECKBOX_ORDER = [
  'ccapsediownedbusiness_borrower_legal_name',
  'ccapsediownedbusiness_lender_loan_number',
  'ccapsediownedbusiness_borrower_date',
  'ccapsediownedbusiness_business_address_in_cdfi_investment_area',
  'ccapsediownedbusiness_borrower_printed_name',
  'ccapsediownedbusiness_borrower_signature',
  'ccapsediownedbusiness_printed_name_authorized_signer',
  'ccapsediownedbusiness_title',
  'ccapsediownedbusiness_lender_date',
  'ccapsediownedbusiness_authorized_lender_signature',
  'ccapsediownedbusiness_individual_address_in_cdfi_investments_area',
  'ccapsediownedbusiness_business_will_open_or_operate_in_cdfi_investment_area',
  'ccapsediownedbusiness_buiness_not_certifying',
  'ccapsediownedbusiness_question_1',
  'ccapsediownedbusiness_question2',
  'ccapsediownedbusiness_question3',
];

const sortFieldsByPreferredKeyOrder = (fields, preferredKeys) => {
  if (!Array.isArray(fields) || !fields.length || !preferredKeys?.length) {
    return fields;
  }
  const orderMap = new Map(preferredKeys.map((k, i) => [k, i]));
  return fields
    .map((f, origIdx) => ({ f, origIdx, k: f.key ?? f.name }))
    .sort((a, b) => {
      const ia = orderMap.has(a.k) ? orderMap.get(a.k) : 10000 + a.origIdx;
      const ib = orderMap.has(b.k) ? orderMap.get(b.k) : 10000 + b.origIdx;
      return ia - ib;
    })
    .map((x) => x.f);
};

/**
 * CalCAP SEDI-Owned Business: keep all checkboxes in one block (3 per row), then text/table fields.
 * Avoids mixing checkbox and text columns in the same grid rows.
 */
export const chunkCcapsediOwnedBusinessFields = (fields) => {
  if (!Array.isArray(fields) || !fields.length) return [];
  const checkboxes = [];
  const others = [];
  for (const f of fields) {
    if (f?.type === 'checkbox') checkboxes.push(f);
    else others.push(f);
  }
  const sortedOthers = sortFieldsByPreferredKeyOrder(others, CCAPSEDI_NON_CHECKBOX_ORDER);
  const out = [];
  if (checkboxes.length) {
    out.push({ kind: 'sediCheckboxGrid', fields: checkboxes });
  }
  for (const f of sortedOthers) {
    out.push({ kind: 'field', field: f });
  }
  return out;
};

/** `page1_*` keys from the SB packet that belong in the CalCAP Use Only section (not a standalone Page1 block). */
const PAGE1_CALCAP_USE_ONLY_KEY_ORDER = [
  'page1_calcapuseonly_date_received',
  'page1_calcapuseonly_csp_number',
  'page1_calcapuseonly_application_number',
  'page1_calcap_use_only_loan_number',
];

const isPage1CalcapUseOnlyFieldKey = (key) => {
  if (typeof key !== 'string' || !key.startsWith('page1_')) return false;
  const rest = key.slice('page1_'.length);
  return rest.startsWith('calcapuseonly_') || rest.startsWith('calcap_use_only_');
};

const CALCAP_USE_ONLY_FUND_KEYS = [
  'calcapuseonly_fund_1',
  'calcapuseonly_fund_2',
  'calcapuseonly_fund_3',
];

const CALCAP_USE_ONLY_PERCENT_KEYS = [
  'calcapuseonly_percentage_1',
  'calcapuseonly_percentage_2',
  'calcapuseonly_percentage_3',
];

/**
 * At the first Fund or Percentage field in document order, emit Fund 1–3 (if any) then
 * Percentage 1–3 (if any), then the rest—so the UI can show two aligned rows.
 */
const reorderCalcapUseOnlyLayout = (fields) => {
  if (!Array.isArray(fields) || !fields.length) return fields;
  const fundSet = new Set(CALCAP_USE_ONLY_FUND_KEYS);
  const pctSet = new Set(CALCAP_USE_ONLY_PERCENT_KEYS);
  const special = new Set([...fundSet, ...pctSet]);
  const byKey = new Map(fields.map((f) => [f.key ?? f.name, f]));
  const fundRow = CALCAP_USE_ONLY_FUND_KEYS.map((k) => byKey.get(k)).filter(Boolean);
  const pctRow = CALCAP_USE_ONLY_PERCENT_KEYS.map((k) => byKey.get(k)).filter(Boolean);

  let inserted = false;
  const out = [];
  for (const f of fields) {
    const k = f.key ?? f.name;
    if (!inserted && special.has(k)) {
      out.push(...fundRow);
      out.push(...pctRow);
      inserted = true;
      continue;
    }
    if (inserted && special.has(k)) continue;
    out.push(f);
  }
  return out;
};

/**
 * CalCAP Use Only: Fund 1–3 on one row, then Percentage 1–3 on the next; remaining fields default grid.
 */
export const chunkCalcapUseOnlyFields = (fields) => {
  if (!Array.isArray(fields) || !fields.length) return [];
  const ordered = reorderCalcapUseOnlyLayout(fields);
  const out = [];
  let i = 0;
  while (i < ordered.length) {
    const k0 = ordered[i]?.key ?? ordered[i]?.name;
    const k1 = ordered[i + 1]?.key ?? ordered[i + 1]?.name;
    const k2 = ordered[i + 2]?.key ?? ordered[i + 2]?.name;
    if (
      k0 === CALCAP_USE_ONLY_FUND_KEYS[0] &&
      k1 === CALCAP_USE_ONLY_FUND_KEYS[1] &&
      k2 === CALCAP_USE_ONLY_FUND_KEYS[2]
    ) {
      out.push({ kind: 'calcapFundRow', fields: ordered.slice(i, i + 3) });
      i += 3;
      continue;
    }
    if (
      k0 === CALCAP_USE_ONLY_PERCENT_KEYS[0] &&
      k1 === CALCAP_USE_ONLY_PERCENT_KEYS[1] &&
      k2 === CALCAP_USE_ONLY_PERCENT_KEYS[2]
    ) {
      out.push({ kind: 'calcapPercentRow', fields: ordered.slice(i, i + 3) });
      i += 3;
      continue;
    }
    out.push({ kind: 'field', field: ordered[i] });
    i++;
  }
  return out;
};

/** Box 1 / Box 2: single dropdown in UI; submit expands to original `_yes` / `_no` checkbox keys (booleans). */
const YES_NO_BOX_SELECT_OPTIONS = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

const BOX_YES_NO_PAIR_GROUPS = [
  {
    prefix: 'box1',
    yesKey: 'box1_does_transaction_include_purchase_or_refinance_of_commercial_realestate_yes',
    noKey: 'box1_does_transaction_include_purchase_or_refinance_of_commercial_realestate_no',
    mergedName: 'box1_does_transaction_include_purchase_or_refinance_of_commercial_realestate',
    label: 'Does Transaction Include Purchase Or Refinance Of Commercial Real Estate',
  },
  {
    prefix: 'box2',
    yesKey: 'box2_is_refinance_debt_held_at_your_institution_yes',
    noKey: 'box2_is_refinance_debt_held_at_your_institution_no',
    mergedName: 'box2_is_refinance_debt_held_at_your_institution',
    label: 'Is Refinance Debt Held At Your Institution',
  },
  {
    prefix: 'box2',
    yesKey: 'box2_is_refinance_of_debt_held_at_another_institution_yes',
    noKey: 'box2_is_refinance_of_debt_held_at_another_institution_no',
    mergedName: 'box2_is_refinance_of_debt_held_at_another_institution',
    label: 'Is Refinance Of Debt Held At Another Institution',
  },
];

const BOX1_TEXT_FIELD_AS_YES_NO_SELECT = 'box1_is_purchase_for_existing_building_or_new_construction';

/**
 * CalCAP CSP (Document Intelligence): Sec 1/2 questionnaire Yes/No pairs use
 * `calcap_sb_csp_questionaire_*` keys. They group under category `calcap` (first `_` segment),
 * not `box1` / `box2`, so they need the same merged-dropdown treatment as BOX_YES_NO_PAIR_GROUPS.
 * Keep in sync with model output (sample: OCR/Untitled-1.json).
 */
const CALCAP_SB_CSP_QUESTIONNAIRE_YES_NO_PAIRS = [
  {
    yesKey:
      'calcap_sb_csp_questionaire_sec1_box1_does_transaction_include_purchase_or_refinance_of_commercial_realestate_yes',
    noKey:
      'calcap_sb_csp_questionaire_sec1_box1_does_transaction_include_purchase_or_refinance_of_commercial_realestate_no',
    mergedName:
      'calcap_sb_csp_questionaire_sec1_box1_does_transaction_include_purchase_or_refinance_of_commercial_realestate',
    label: 'Does Transaction Include Purchase Or Refinance Of Commercial Real Estate',
  },
  {
    yesKey: 'calcap_sb_csp_questionaire_1_sec2_is_refinance_debt_held_at_your_institution_yes',
    noKey: 'calcap_sb_csp_questionaire_1_sec2_is_refinance_debt_held_at_your_institution_no',
    mergedName: 'calcap_sb_csp_questionaire_1_sec2_is_refinance_debt_held_at_your_institution',
    label: 'Is Refinance Debt Held At Your Institution',
  },
  {
    yesKey:
      'calcap_sb_csp_questionaire_1_sec2_is_refinance_of_debt_held_at_another_institution_yes',
    noKey:
      'calcap_sb_csp_questionaire_1_sec2_is_refinance_of_debt_held_at_another_institution_no',
    mergedName:
      'calcap_sb_csp_questionaire_1_sec2_is_refinance_of_debt_held_at_another_institution',
    label: 'Is Refinance Of Debt Held At Another Institution',
  },
  {
    yesKey:
      'calcap_sb_csp_questionaire_1_sec2_is_original_debit_enrolled_in_calcapcollateralsupport_or_calcapsmallbusiness_yes',
    noKey:
      'calcap_sb_csp_questionaire_1_sec2_is_original_debit_enrolled_in_calcapcollateralsupport_or_calcapsmallbusiness_no',
    mergedName:
      'calcap_sb_csp_questionaire_1_sec2_is_original_debit_enrolled_in_calcapcollateralsupport_or_calcapsmallbusiness',
    label:
      'Is Original Debt Enrolled In CalCAP Collateral Support Or CalCAP Small Business',
  },
];

const CALCAP_SB_CSP_BOX1_TEXT_AS_YES_NO_SELECT =
  'calcap_sb_csp_questionaire_sec1_is_purchase_for_existing_building_or_new_construction';

const deriveYesNoPairSelectValue = (yesField, noField) => {
  const y = yesField?.value === true;
  const n = noField?.value === true;
  if (y && !n) return 'Yes';
  if (n && !y) return 'No';
  if (y && n) return 'Yes';
  return null;
};

const normalizeOcrStringToYesNoSelect = (v) => {
  if (v == null || v === '') return null;
  const s = String(v).trim().toLowerCase();
  if (s === 'yes' || s === 'y' || s === 'true') return 'Yes';
  if (s === 'no' || s === 'n' || s === 'false') return 'No';
  return String(v).trim();
};

/**
 * Merges paired Yes/No selection marks into one Yes/No dropdown per question.
 * `formatOcrDataForSubmit` expands `_ocrYesNoPairKeys` back to the original API keys with booleans.
 * @param {string|null|undefined} textFieldKeyForSelect - optional text field to coerce to Yes/No select (Box 1 pattern)
 */
const mergeYesNoPairsIntoSelects = (fields, pairs, textFieldKeyForSelect) => {
  if (!Array.isArray(fields) || !fields.length) return fields;
  if (!pairs?.length) {
    if (!textFieldKeyForSelect) return fields;
    return fields.map((f) => {
      const k = f.key ?? f.name;
      if (k === textFieldKeyForSelect && f.type === 'text-input') {
        return {
          ...f,
          type: 'select',
          options: YES_NO_BOX_SELECT_OPTIONS,
          value: normalizeOcrStringToYesNoSelect(f.value),
        };
      }
      return f;
    });
  }

  const byKey = new Map(fields.map((f) => [f.key ?? f.name, f]));
  const mergedRows = [];
  for (const pair of pairs) {
    const yesF = byKey.get(pair.yesKey);
    const noF = byKey.get(pair.noKey);
    if (!yesF && !noF) continue;
    mergedRows.push({
      key: pair.mergedName,
      name: pair.mergedName,
      label: pair.label,
      type: 'select',
      options: YES_NO_BOX_SELECT_OPTIONS,
      value: deriveYesNoPairSelectValue(yesF, noF),
      _ocrYesNoPairKeys: { yesKey: pair.yesKey, noKey: pair.noKey },
    });
  }

  const mergedByName = new Map(mergedRows.map((m) => [m.key, m]));
  const out = [];
  const insertedMerged = new Set();
  const pairKeySet = new Set();
  pairs.forEach((p) => {
    pairKeySet.add(p.yesKey);
    pairKeySet.add(p.noKey);
  });

  for (const f of fields) {
    const k = f.key ?? f.name;
    if (pairKeySet.has(k)) {
      const pair = pairs.find((p) => p.yesKey === k || p.noKey === k);
      if (pair && !insertedMerged.has(pair.mergedName)) {
        const mf = mergedByName.get(pair.mergedName);
        if (mf) {
          out.push(mf);
          insertedMerged.add(pair.mergedName);
        }
      }
      continue;
    }
    out.push(f);
  }

  if (!textFieldKeyForSelect) return out;

  return out.map((f) => {
    const k = f.key ?? f.name;
    if (k === textFieldKeyForSelect && f.type === 'text-input') {
      return {
        ...f,
        type: 'select',
        options: YES_NO_BOX_SELECT_OPTIONS,
        value: normalizeOcrStringToYesNoSelect(f.value),
      };
    }
    return f;
  });
};

const mergeBoxYesNoSelectFields = (fields, prefix) => {
  if (!Array.isArray(fields) || !fields.length) return fields;
  if (prefix !== 'box1' && prefix !== 'box2') return fields;

  const pairs = BOX_YES_NO_PAIR_GROUPS.filter((p) => p.prefix === prefix).map(
    ({ yesKey, noKey, mergedName, label }) => ({ yesKey, noKey, mergedName, label })
  );
  return mergeYesNoPairsIntoSelects(
    fields,
    pairs,
    prefix === 'box1' ? BOX1_TEXT_FIELD_AS_YES_NO_SELECT : null
  );
};

/** Merges CalCAP CSP questionnaire Yes/No pairs living under the `calcap` category prefix. */
const mergeCalcapSbCspQuestionnaireYesNoFields = (fields) =>
  mergeYesNoPairsIntoSelects(
    fields,
    CALCAP_SB_CSP_QUESTIONNAIRE_YES_NO_PAIRS,
    CALCAP_SB_CSP_BOX1_TEXT_AS_YES_NO_SELECT
  );

/**
 * Pulls minority / woman / veteran / severely-affected checkboxes into one contiguous block
 * (after the field that preceded the first of those in document order).
 */
const groupBorrowerOwnershipCheckboxFields = (fields) => {
  if (!Array.isArray(fields) || !fields.length) return fields;
  const ownership = fields.filter(isBorrowerOwnershipGroupField);
  if (ownership.length === 0) return fields;
  const firstIdx = fields.findIndex(isBorrowerOwnershipGroupField);
  const head = fields.slice(0, firstIdx);
  const tail = fields.slice(firstIdx + 1).filter((f) => !isBorrowerOwnershipGroupField(f));
  const suffixOrder = (field) => {
    const key = field.key ?? field.name;
    const after = compactOcrFieldKey(key).slice('borrowerinformation'.length);
    return BORROWER_OWNERSHIP_GROUP_SUFFIXES.indexOf(after);
  };
  const sortedBlock = [...ownership].sort((a, b) => suffixOrder(a) - suffixOrder(b));
  return [...head, ...sortedBlock, ...tail];
};



export const parseOcrData = () => {
  const { rawOcrData, modelId } = ocrSignal.value;
  if (!rawOcrData?.fields) {
    return;
  }


  const { parsedOcrData, sortedCategories } = parseOcrDataFromRaw(
    rawOcrData,
    modelId
  );

  /** Keep `modelId` on the signal so category order stays tied to the program’s OCR model. */
  ocrSignal.update({ parsedOcrData, sortedCategories, modelId });
};

/** CalCAP full application templates are ~22 pages. */
export const CALCAP_EXPECTED_PAGE_COUNT = 22;

const CALCAP_OCR_MODEL_IDS = new Set([
  'Calcap_SB_revised_4_2026',
  'Calcap_CSP_revised_4_2026',
  'cpcfa-calcap-sb',
  'cpcfa-calcap-csp',
]);

export const getExpectedOcrPageCount = (modelId = '') =>
  CALCAP_OCR_MODEL_IDS.has(modelId) ? CALCAP_EXPECTED_PAGE_COUNT : null;

/** Highest page number referenced by any field bounding region. */
export const getOcrMaxPageNumber = (rawOcrData) => {
  if (!rawOcrData?.fields) return 0;
  let maxPage = 0;
  Object.values(rawOcrData.fields).forEach((field) => {
    (field?.boundingRegions ?? []).forEach((region) => {
      const page = Number(region?.pageNumber);
      if (Number.isFinite(page) && page > maxPage) maxPage = page;
    });
  });
  return maxPage;
};

const isMeaningfulRawOcrFieldValue = (field) => {
  if (!field || typeof field !== 'object') return false;
  const type = field.type;
  if (type === 'selectionMark') {
    const mark = field.valueSelectionMark ?? field.content;
    return mark === 'selected' || mark === ':selected:';
  }
  if (type === 'array') {
    const rows = field.valueArray ?? field.value?.rows ?? field.values;
    return Array.isArray(rows) && rows.length > 0;
  }
  const text = coalesceValue(
    field.valueString,
    field.valueNumber,
    field.valueDate,
    field.value,
    field.content
  );
  if (text == null || text === '') return false;
  const normalized = String(text).trim().toLowerCase();
  return normalized !== '' && normalized !== ':unselected:' && normalized !== 'unselected';
};

/** Count OCR fields that contain user-entered data (not blank template marks). */
export const countMeaningfulOcrFields = (rawOcrData) => {
  if (!rawOcrData?.fields) return 0;
  return Object.values(rawOcrData.fields).filter(isMeaningfulRawOcrFieldValue).length;
};

/**
 * Distinguish intentional partial uploads from unreadable scans.
 * Document confidence alone is not used for data loss — only for bad-scan heuristics.
 * @returns {'complete' | 'partial' | 'badScan'}
 */
export const classifyOcrIntake = (rawOcrData, modelId = '') => {
  const meaningfulCount = countMeaningfulOcrFields(rawOcrData);
  const totalFieldCount = Object.keys(rawOcrData?.fields ?? {}).length;
  const maxPage = getOcrMaxPageNumber(rawOcrData);
  const expectedPages = getExpectedOcrPageCount(modelId);

  if (meaningfulCount === 0) {
    return 'badScan';
  }

  if (meaningfulCount <= 3 && totalFieldCount <= 10) {
    return 'badScan';
  }

  if (expectedPages && maxPage > 0 && maxPage < expectedPages * 0.6) {
    return meaningfulCount >= 3 ? 'partial' : 'badScan';
  }

  if (
    expectedPages &&
    maxPage > 0 &&
    maxPage < expectedPages * 0.85 &&
    meaningfulCount >= 5
  ) {
    return 'partial';
  }

  const confidence = Number(rawOcrData?.confidence);
  if (
    Number.isFinite(confidence) &&
    confidence < 0.3 &&
    meaningfulCount < 10 &&
    (!expectedPages || maxPage < expectedPages * 0.3)
  ) {
    return 'badScan';
  }

  return 'complete';
};

const sortCategoriesByPdfOrder = (categories, modelId) => {
  const order =
    modelId === 'Calcap_CSP_revised_4_2026'
      ? CALCAP_CSP_CATEGORY_ORDER
      : modelId === 'Calcap_SB_revised_4_2026'
        ? CALCAP_SB_CATEGORY_ORDER
        : null;
  if (!order) return categories;
  return [...categories].sort((a, b) => {
    const ai = order.indexOf(a);
    const bi = order.indexOf(b);
    if (ai >= 0 && bi >= 0) return ai - bi;
    if (ai >= 0) return -1;
    if (bi >= 0) return 1;
    return a.localeCompare(b);
  });
};

/**
 * Category order for OCR UI and persisted `sortedCategories`. Derived from `parsedOcrData` keys
 * plus model id so layout stays correct even if object key order differs.
 */
export const getOcrDisplayCategoryOrder = (parsedOcrData, modelId = '') => {
  const keys = Object.keys(parsedOcrData ?? {});
  if (
    !keys.length ||
    (modelId !== 'Calcap_SB_revised_4_2026' &&
      modelId !== 'Calcap_CSP_revised_4_2026')
  ) {
    return keys;
  }
  return sortCategoriesByPdfOrder(keys, modelId);
};

/** OCR sections hidden from the application form UI (all CalCAP models). */
const OCR_EXCLUDED_DISPLAY_CATEGORIES = new Set([
  'box3',
  'calcapuseonly',
  'calcap',
  'calcapborroweragreement',
  'calpapborroweragreement',
]);

/**
 * CSP-only sections that appear when the SB packet is OCR'd (embedded CSP pages).
 * Hide on Small Business intake so Supplemental SAC stays with SB content.
 * Do not hide on CSP program OCR — collateral is a real CSP section there.
 */
const OCR_EXCLUDED_SB_CSP_SECTIONS = new Set([
  'collateral',
  'calcapsbcspquestionaire',
  'calcapsbcspquestionnaire',
]);

const isCalcapSbOcrModel = (modelId = '') =>
  modelId === 'Calcap_SB_revised_4_2026' || modelId === 'cpcfa-calcap-sb';

/**
 * Fields hidden from OCR Application Form UI only (data may still exist in formData).
 * Lender Cert "CalCAP Loan Number" is the lender loan # field mislabeled — remove from that section;
 * CalCAP loan # remains elsewhere (header / Application Details / use-only when shown).
 */
const OCR_EXCLUDED_DISPLAY_FIELD_KEYS = new Set([
  'calcaplendercertification_lender_loan_number',
  'calpaplendercertification_lender_loan_number',
]);

export const shouldIncludeOcrFieldForDisplay = (fieldKey) => {
  if (fieldKey == null || fieldKey === '') return true;
  return !OCR_EXCLUDED_DISPLAY_FIELD_KEYS.has(String(fieldKey).toLowerCase());
};

export const shouldIncludeOcrCategoryForDisplay = (category, modelId = '') => {
  if (category == null) {
    return true;
  }
  const key = String(category).toLowerCase();
  if (OCR_EXCLUDED_DISPLAY_CATEGORIES.has(key)) {
    return false;
  }
  if (isCalcapSbOcrModel(modelId) && OCR_EXCLUDED_SB_CSP_SECTIONS.has(key)) {
    return false;
  }
  return true;
};

const isSelectedMarkValue = (v) =>
  v === true ||
  v === 'selected' ||
  v === ':selected:' ||
  (typeof v === 'string' && v.trim().toLowerCase() === 'yes');

/** Ordered category keys for UI and `sortedCategories`, after visibility rules. */
export const getOcrCategoriesToRender = (parsedOcrData, modelId = '') => {
  const ordered = getOcrDisplayCategoryOrder(parsedOcrData, modelId);
  return ordered.filter((cat) =>
    shouldIncludeOcrCategoryForDisplay(cat, modelId)
  );
};

/**
 * Parses raw OCR API response into { parsedOcrData, sortedCategories } for use with OCRRender.
 * Order matches PDF layout: uses boundingRegions when available; otherwise uses predefined
 * CalCAP SB order (General info, Lender info, Borrower info, Loan info, etc.).
 * @param {Object} rawOcrData - Raw OCR response with .fields
 * @param {string} [modelId] - Optional model id (e.g. 'cpcfa-calcap-sb') for sort order
 * @returns {{ parsedOcrData: Object, sortedCategories: string[] }}
 */
export const parseOcrDataFromRaw = (rawOcrData, modelId = '') => {
  if (!rawOcrData?.fields) {
    return { parsedOcrData: {}, sortedCategories: [] };
  }


  const fieldNames = Object.keys(rawOcrData.fields);
  const hasBoundingRegions = fieldNames.some(
    (fn) => rawOcrData.fields[fn]?.boundingRegions?.length
  );

  let sorted;
  if (hasBoundingRegions) {
    sorted = fieldNames.sort((fieldA, fieldB) => {
      const a = rawOcrData.fields[fieldA];
      const b = rawOcrData.fields[fieldB];
      if (!a.boundingRegions && b.boundingRegions) return 1;
      if (a.boundingRegions && !b.boundingRegions) return -1;
      if (a.boundingRegions && b.boundingRegions) {
        if (a.boundingRegions[0]?.pageNumber === b.boundingRegions[0]?.pageNumber) {
          return (a.boundingRegions[0]?.polygon[0]?.y ?? 0) - (b.boundingRegions[0]?.polygon[0]?.y ?? 0);
        }
        return (a.boundingRegions[0]?.pageNumber ?? 0) - (b.boundingRegions[0]?.pageNumber ?? 0);
      }
      return 1;
    });
  } else {
    sorted = fieldNames;
  }

  if (modelId === 'Calcap_CSP_revised_4_2026') {
    sorted = sorted.filter(
      (fn) =>
        !fn.startsWith('calcapuseonly_') &&
        // Keep only rendered detections for CSP (drops metadata-only placeholders).
        Array.isArray(rawOcrData.fields?.[fn]?.boundingRegions) &&
        rawOcrData.fields[fn].boundingRegions.length > 0
    );
  }

  const groupedByPrefix = sorted.reduce((acc, fieldName) => {
    const normalizedFieldName =
      modelId === 'Calcap_CSP_revised_4_2026' && fieldName === 'loaninformation_loan_purpose'
        ? 'borrowerinformation_loan_purpose'
        : fieldName;
    const prefix = normalizedFieldName.split('_')[0];
    if (!acc[prefix]) acc[prefix] = [];
    // Spread first so API `key` cannot overwrite the canonical `fieldName` from Object.keys.
    acc[prefix].push({ ...rawOcrData.fields[fieldName], key: normalizedFieldName });
    return acc;
  }, {});


  const parsedOcrData = {};
  for (const [prefix, rawFields] of Object.entries(groupedByPrefix)) {
    const transformed = rawFields.map((field) => transformFieldForForm(field));
    const collapsed = collapseSelectFields(transformed);
    parsedOcrData[prefix] =
      prefix === 'borrowerinformation'
        ? groupBorrowerOwnershipCheckboxFields(collapsed)
        : prefix === 'box1' || prefix === 'box2'
          ? mergeBoxYesNoSelectFields(collapsed, prefix)
          : prefix === 'calcap'
            ? mergeCalcapSbCspQuestionnaireYesNoFields(collapsed)
            : collapsed;
  }

  if (parsedOcrData.supplementalsaccertification?.length) {
    parsedOcrData.supplementalsaccertification = sortFieldsByPreferredKeyOrder(
      parsedOcrData.supplementalsaccertification,
      SUPPLEMENTAL_SAC_CERT_FIELD_ORDER
    );
  }

  const page1List = parsedOcrData.page1;
  if (Array.isArray(page1List) && page1List.length) {
    const page1Calcap = [];
    const page1Rest = [];
    for (const f of page1List) {
      const k = f?.key ?? f?.name;
      if (isPage1CalcapUseOnlyFieldKey(k)) page1Calcap.push(f);
      else page1Rest.push(f);
    }
    if (page1Calcap.length) {
      const sortedHeader = sortFieldsByPreferredKeyOrder(
        page1Calcap,
        PAGE1_CALCAP_USE_ONLY_KEY_ORDER
      );
      parsedOcrData.calcapuseonly = [
        ...sortedHeader,
        ...(parsedOcrData.calcapuseonly ?? []),
      ];
    }
    if (page1Rest.length) {
      parsedOcrData.page1 = page1Rest;
    } else {
      delete parsedOcrData.page1;
    }
  }

  if (parsedOcrData.calcapuseonly?.length) {
    parsedOcrData.calcapuseonly = reorderCalcapUseOnlyLayout(
      parsedOcrData.calcapuseonly
    );
  }

  const RACE_SEP = ';';
  const mergedDemographics = DEMOGRAPHICS_CATEGORY_PREFIXES.reduce((acc, prefix) => {
    const fields = parsedOcrData[prefix];
    if (prefix === 'race' && fields?.length) {
      const raceOptionByOcrKey = new Map(
        OCR_SELECT_OPTIONS.race.map((o) => [String(o.ocrKey).toLowerCase(), o.value])
      );
      const selectedValues = fields
        .filter((f) => isSelectedMarkValue(f?.value))
        .map((f) => raceOptionByOcrKey.get(String(f?.key ?? '').toLowerCase()))
        .filter(Boolean);
      acc.push({
        key: 'race',
        name: 'race',
        label: 'Race',
        type: 'multi-select-dropdown',
        value: selectedValues.length ? selectedValues.join(RACE_SEP) : null,
        options: OCR_SELECT_OPTIONS.race,
      });
    } else if (fields?.length) {
      acc.push(...fields);
    }
    if (prefix !== 'borrowerdemographics') delete parsedOcrData[prefix];
    return acc;
  }, []);



  if (mergedDemographics.length) {
    parsedOcrData.borrowerdemographics = mergedDemographics;
  }

  // Keep Supplemental SAC section; force blank values on OCR intake (Jul 23).
  const withBlankSupplementalSac =
    blankSupplementalSacCertificationFields(parsedOcrData);
  const withSignatureConfirm = applyAnalystSignatureConfirmFields(
    withBlankSupplementalSac
  );
  const sortedCategories = getOcrCategoriesToRender(withSignatureConfirm, modelId);

  return { parsedOcrData: withSignatureConfirm, sortedCategories };
};

export const onOcrFieldChange = (e, category) => {
  const { name, value } = e;
  const { parsedOcrData } = ocrSignal.value;

  const updatedFields = (parsedOcrData[category] ?? []).map((field) =>
    (field.name === name || field.key === name) ? { ...field, value } : field
  );

  ocrSignal.update({
    parsedOcrData: {
      ...parsedOcrData,
      [category]: updatedFields,
    },
    formattedOcrData: {},
    normalizedOcrData: {},
  });
};

export const onOcrTableFieldChange = (e, category, tableName, rowIndex) => {
  const { name, value } = e;
  const { parsedOcrData } = ocrSignal.value;

  const updatedFields = (parsedOcrData[category] ?? []).map((field) => {
    if (field.name !== tableName && field.key !== tableName) return field;
    const rows = [...(field.value?.rows ?? [])];
    if (rows[rowIndex]) rows[rowIndex] = { ...rows[rowIndex], [name]: value };
    return { ...field, value: { ...field.value, rows } };
  });

  ocrSignal.update({
    parsedOcrData: {
      ...parsedOcrData,
      [category]: updatedFields,
    },
    formattedOcrData: {},
    normalizedOcrData: {},
  });
};

export const onOcrCheckboxChange = (e, category) => {
  const { name } = e;
  let { value } = e;
  const { parsedOcrData } = ocrSignal.value;
  const newValue = value.toString() === 'true' ? false : true;

  const updatedFields = (parsedOcrData[category] ?? []).map((field) =>
    (field.name === name || field.key === name) ? { ...field, value: newValue } : field
  );

  ocrSignal.update({
    parsedOcrData: {
      ...parsedOcrData,
      [category]: updatedFields,
    },
    formattedOcrData: {},
    normalizedOcrData: {},
  });
};

const pickFirst = (data, keys) =>
  keys.map((k) => data[k]).find((v) => v != null && v !== '');

const pickFirstNumber = (data, keys) => {
  const raw = pickFirst(data, keys);
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : undefined;
};

/** True if the race multi-select value string contains the given option (e.g. 'White'). */
const raceMultiSelectIncludes = (raceValue, optionValue) => {
  if (!raceValue || typeof raceValue !== 'string') return false;
  const parts = raceValue.split(';').map((s) => (s || '').trim()).filter(Boolean);
  return parts.includes(optionValue);
};

export const normalizeOcrData = (formattedOcrData) => {
  const ocrData = formattedOcrData;
  const parsedJobsCreated = pickFirstNumber(ocrData, [
    'borrowerinformation_jobs_created',
    'borrowerInformation_jobsCreated',
  ]);
  const parsedJobsRetained = pickFirstNumber(ocrData, [
    'borrowerinformation_jobs_retained',
    'borrowerInformation_jobsRetained',
  ]);
  const parsedNumberOfFtes = pickFirstNumber(ocrData, [
    'borrowerinformation_num_fte',
    'borrowerInformation_num_fte',
    'borrowerinformation_number_of_ftes',
    'borrowerInformation_numberOfFtes',
    // Fallback: many templates only provide number of employees.
    'borrowerinformation_num_employees',
    'borrowerInformation_numberOfEmployees',
    'borrowerinformation_number_of_employees',
  ]);
  const parsedNumberOfEmployees = pickFirstNumber(ocrData, [
    'borrowerinformation_num_employees',
    'borrowerInformation_numberOfEmployees',
    'borrowerinformation_number_of_employees',
    'borrowerInformation_num_employees',
  ]);

  return {
    businessName: pickFirst(ocrData, [
      'borrowerinformation_business_name',
      'borrowerInformation_businessName',
      'calcapborrowercertification_businessname',
      'borrowercertificationforsmallbusinessloanenrollments_businessname',
      'borrowerCertificationForSmallBusinessLoanEnrollments_businessName',
      'calcapborroweragreement_business_name',
    ]),
    businessEin: pickFirst(ocrData, [
      'borrowerInformation_ein',
      'borrowerinformation_ein_or_tax_number',
      'borroweinformation_ein_or_tax_number',
    ]),
    industry: pickFirst(ocrData, [
      'borrowerInformation_industry',
      'borrowerinformation_business_type',
    ]),
    businessAddress: pickFirst(ocrData, [
      'borrowerInformation_businessAddressMailing',
      'borrowerinformation_business_address',
      'borrowerinformation_address_monies_used_at',
      'borrowerinformation_address_money_used_at',
      'ccapsediownedbusiness_business_address_in_cdfi_investment_area',
    ]),
    lenderEin: ocrData?.lenderinformation_ein_or_tax_number,
    businessCity: pickFirst(ocrData, [
      'borrowerInformation_city',
      'borrowerinformation_business_city',
    ]),
    businessAddressLine1: pickFirst(ocrData, [
      'borrowerinformation_business_address',
      'borrowerinformation_business_address_line_1',
      'borrowerinformation_address_monies_used_at',
      'borrowerinformation_address_money_used_at',
    ]),
    businessCounty: pickFirst(ocrData, [
      'borrowerInformation_county',
      'borrowerinformation_business_county',
    ]),
    businessState: pickFirst(ocrData, [
      'borrowerInformation_state',
      'borrowerinformation_business_state',
    ]),
    businessZipcode: pickFirst(ocrData, [
      'borrowerInformation_zipcode',
      'borrowerinformation_business_zip',
    ]),
    businessType: pickFirst(ocrData, [
      'borrowerInformation_businessDescription',
      'borrowerinformation_business_type',
      'borrowerinformation_business_org_type',
    ]),
    censusTractNumber: pickFirst(ocrData, [
      'borrowerinformation_census_tract_number',
      'borrowerInformation_censusTractNumber',
    ]),
    contactName: pickFirst(ocrData, [
      'borrowerInformation_contactName',
      'businessInformation_contactName',
      'borrowerInformation_name',
      'borrowerinformation_name',
      'borrowerdemographics_legal_borrower_name',
      'calcapborrowercertification_individual_name',
      'ccapsediownedbusiness_borrower_printed_name',
    ]),
    contactPhone: pickFirst(ocrData, [
      'borrowerInformation_contactPhone',
      'businessInformation_phone',
      'businessInformation_contactPhone',
      'businessInformation_phoneNumber',
      'lenderinformation_contact_phone_number',
      'lendinginformation_contact_phone_number',
    ]),
    contactEmail: pickFirst(ocrData, [
      'borrowerInformation_contactEmail',
      'borrowerInformation_email',
      'businessInformation_email',
      'businessInformation_emailAddress',
      'lenderinformation_contact_email',
    ]),
    naicsCode: pickFirst(ocrData, [
      'borrowerInformation_naicsCode',
      'businessInformation_naics',
      'businessInformation_naicsCode',
      'borrowerInformation_naics',
      'borrowerinformation_naics_code',
    ]),
    businessOpened2Years: (() => {
      const startYear = +ocrData['borrowerinformation_business_start_year'] || null;
      if (startYear == null) return undefined;
      const yearsSinceStart = new Date().getFullYear() - startYear;
      return yearsSinceStart >= 2;
    })(),
    numberOfFtes: parsedNumberOfFtes,
    numberOfEmployees: parsedNumberOfEmployees,
    /** Prefer OCR jobs created/retained fields; fallback to employee headcount when jobs are absent. */
    numberOfJobs: (() => {
      const hasJobs =
        Number.isFinite(parsedJobsCreated) || Number.isFinite(parsedJobsRetained);
      if (hasJobs) {
        return (parsedJobsCreated || 0) + (parsedJobsRetained || 0);
      }
      if (Number.isFinite(parsedNumberOfEmployees)) return parsedNumberOfEmployees;
      return 0;
    })(),
    annualRevenue: (() => {
      const raw = pickFirst(ocrData, [
        'borrowerinformation_annual_revenue_last_year',
        'borrowerinformation_annual_revenue_last_3year',
      ]);
      if (raw == null || raw === '') return undefined;
      if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
      const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
      return Number.isNaN(n) ? undefined : n;
    })(),
    isWomanOwned:
      ocrData?.ssbciDemographics_womenOwnedBusiness === 'yes' ||
      ocrYes(
        pickFirst(ocrData, [
          'borrowerinformation_is_woman_owned',
          'borrowerInformation_is_woman_owned',
          'borrowerInformation_isWomanOwned',
        ])
      ),
    isMinorityOwned:
      ocrData?.ssbciDemographics_minorityOwnedBusiness === 'yes' ||
      ocrYes(
        pickFirst(ocrData, [
          'borrowerinformation_is_minority_owned',
          'borrowerInformation_is_minority_owned',
          'borrowerInformation_isMinorityOwned',
        ])
      ),
    severelyAffected: (() => {
      const v = pickFirst(ocrData, [
        'borrowerinformation_severely_affected',
        'borrowerInformation_severely_affected',
      ]);
      if (v === undefined || v === null || v === '') return undefined;
      return ocrYes(v);
    })(),
    demographics: {
      isVeteranOwned:
        ocrData?.ssbciDemographics_veteranOwnedBusiness === 'yes' ||
        ocrYes(
          pickFirst(ocrData, [
            'borrowerinformation_is_veteran_owned',
            'borrowerInformation_is_veteran_owned',
            'borrowerInformation_isVeteranOwned',
          ])
        ),
      ethnicityHispanicOrLatino: ocrData?.ethnicity_hispanicOrLatino,
      racePreferNotToRespond:
        !!ocrData?.ssbciDemographics_raceNotRespond ||
        !!ocrData?.race_prefer_not_to_respond ||
        raceMultiSelectIncludes(ocrData?.race, 'Prefer not to respond'),
      raceAmericanIndianOrAlaskaNative:
        !!ocrData?.ssbciDemographics_raceAmericanIndianAlaska ||
        !!ocrData?.race_american_indian_or_alaskan_native ||
        raceMultiSelectIncludes(ocrData?.race, 'American Indian or Alaska Native'),
      raceBlackOrAfricanAmerican:
        !!ocrData?.ssbciDemographics_raceBlankAfricanAmerican ||
        !!ocrData?.race_black_or_african_american ||
        raceMultiSelectIncludes(ocrData?.race, 'Black or African American'),
      raceAsianChinese:
        !!ocrData?.ssbciDemographics_raceChinese ||
        !!ocrData?.race_chinese ||
        raceMultiSelectIncludes(ocrData?.race, 'Chinese'),
      raceAsianFilipino:
        !!ocrData?.ssbciDemographics_raceFilipino ||
        !!ocrData?.race_filipino ||
        raceMultiSelectIncludes(ocrData?.race, 'Filipino'),
      raceAsianIndian:
        !!ocrData?.ssbciDemographics_raceAsianIndian ||
        !!ocrData?.race_indian ||
        raceMultiSelectIncludes(ocrData?.race, 'Indian'),
      raceAsianJapanese:
        !!ocrData?.ssbciDemographics_raceJapanese ||
        !!ocrData?.race_japanese ||
        raceMultiSelectIncludes(ocrData?.race, 'Japanese'),
      raceAsianKorean:
        !!ocrData?.ssbciDemographics_raceKorean ||
        !!ocrData?.race_korean ||
        raceMultiSelectIncludes(ocrData?.race, 'Korean'),
      raceAsianVietnamese:
        !!ocrData?.ssbciDemographics_raceVietnamese ||
        !!ocrData?.race_vietnamese ||
        raceMultiSelectIncludes(ocrData?.race, 'Vietnamese'),
      raceAsianOther:
        !!ocrData?.ssbciDemographics_raceAsianOther ||
        !!ocrData?.race_asian_other ||
        raceMultiSelectIncludes(ocrData?.race, 'Asian (Other)'),
      raceGuamanianOrChamorro:
        !!ocrData?.ssbciDemographics_raceGuamanianChamorro ||
        !!ocrData?.race_guamanian_or_chamarro ||
        raceMultiSelectIncludes(ocrData?.race, 'Guamanian or Chamorro'),
      raceNativeHawaiian:
        !!ocrData?.ssbciDemographics_raceNativeHawaiian ||
        !!ocrData?.race_native_hawaiian ||
        !!ocrData?.race_native_hawaiian_or_other_pacific_islander ||
        raceMultiSelectIncludes(ocrData?.race, 'Native Hawaiian') ||
        raceMultiSelectIncludes(ocrData?.race, 'Native Hawaiian or Other Pacific Islander'),
      raceSamoan:
        !!ocrData?.ssbciDemographics_raceSamoan ||
        !!ocrData?.race_samoan ||
        raceMultiSelectIncludes(ocrData?.race, 'Samoan'),
      racePacificIslanderOther:
        !!ocrData?.ssbciDemographics_racePacificIslander ||
        !!ocrData?.race_pacific_islander ||
        raceMultiSelectIncludes(ocrData?.race, 'Pacific Islander(Other)'),
      raceWhite:
        !!ocrData?.ssbciDemographics_raceWhite ||
        !!ocrData?.race_white ||
        raceMultiSelectIncludes(ocrData?.race, 'White'),
      menaf: ocrData?.ancestry_middleEasternOrNorthAfrican,
      gender: (() => {
        const val = pickFirst(ocrData, ['gender_gender', 'gender_self_description']);
        if (val != null && val !== '') return val;
        // Fallback: derive from raw OCR checkbox fields when collapsed gender_gender is missing
        if (ocrData?.gender_female === true || ocrData?.gender_female === 'selected') return 'female';
        if (ocrData?.gender_male === true || ocrData?.gender_male === 'selected') return 'male';
        if (ocrData?.gender_nonbinary === true || ocrData?.gender_nonbinary === 'selected') return 'nonBinary';
        if (ocrData?.gender_prefer_to_selfdescribe === true || ocrData?.gender_prefer_to_selfdescribe === 'selected') return 'decline';
        if (ocrData?.ssbciDemographics_genderFemale === true || ocrData?.ssbciDemographics_genderFemale === 'selected') return 'female';
        if (ocrData?.ssbciDemographics_genderMale === true || ocrData?.ssbciDemographics_genderMale === 'selected') return 'male';
        if (ocrData?.ssbciDemographics_genderNonBinary === true || ocrData?.ssbciDemographics_genderNonBinary === 'selected') return 'nonBinary';
        if (ocrData?.ssbciDemographics_genderNotRespond === true || ocrData?.ssbciDemographics_genderNotRespond === 'selected') return 'decline';
        return val;
      })(),
      genderSelfDescribed:
        ocrData?.ssbciDemographics_genderPreferSelfDescribeDescription ||
        ocrData?.gender_self_description ||
        '',
      sexualOrientation: ocrData?.sexualOrientation_sexualOrientation,
      veteranStatus: ocrData?.veteranStatus_veteranStatus,
    },
    website: ocrData?.businessInformation_businessWebsite || '',
    totalLoanAmount: pickFirst(ocrData, [
      'loanInformation_totalLoanAmount',
      'loaninformation_total_loan_amount',
      'collateral_support_info_proposed_loan_amount',
    ]),
    loanAmountEnrolledInCalcap: pickFirst(ocrData, [
      'loanInformation_loanAmountEnrolledInCalcap',
      'loaninformation_loan_amount_enrolled_in_calcap',
      'loaninformation_loan_amount_enrolled_in_calpap',
    ]),
    loanPurpose: pickFirst(ocrData, [
      'loanPurpose',
      'borrowerinformation_loan_purpose',
    ]),
    loanType: (() => {
      const raw = pickFirst(ocrData, [
        'loanType',
        'loaninformation_loan_type',
      ]);
      if (!raw) return raw;
      return normalizeLoanTypeOcrValue(raw) ?? raw;
    })(),
    refinancing: (() => {
      const v =
        ocrData?.loaninformation_refinancing ??
        ocrData?.refinancing ??
        (ocrData?.box2_is_refinance_debt_held_at_your_institution_yes === true ||
          ocrData?.box2_is_refinance_of_debt_held_at_another_institution_yes === true ||
          ocrData?.calcap_sb_csp_questionaire_1_sec2_is_refinance_debt_held_at_your_institution_yes ===
          true ||
          ocrData?.calcap_sb_csp_questionaire_1_sec2_is_refinance_of_debt_held_at_another_institution_yes ===
          true);
      if (v === true || v === 'Yes' || v === 'yes' || v === 'true') return true;
      if (v === false || v === 'No' || v === 'no' || v === 'false') return false;
      return v;
    })(),
    secured: (() => {
      const v = ocrData?.loaninformation_is_loan_secured ?? ocrData?.loanInformation_secured ?? ocrData?.loaninformation_secured ?? ocrData?.secured;
      if (v === true || v === 'Yes' || v === 'yes' || v === 'true') return true;
      if (v === false || v === 'No' || v === 'no' || v === 'false') return false;
      return v;
    })(),
    calcapLoanNumber: null,
    lenderLoanNumber: pickFirst(ocrData, [
      'lenderLoanNumber',
      'loaninformation_lender_loan_number',
      'calcaplendercertification_lender_loan_number',
      'collateral_support_info_financial_institution_loan_number',
      'calcapborrowercertification_lender_loan_number',
      'calcapborroweragreement_lender_loan_number',
      'borrowerdemographics_lender_loan_number',
      'ccapsediownedbusiness_lender_loan_number',
    ]),
    loaId: pickFirst(ocrData, ['loanInformation_loaId', 'loaninformation_loa_id', 'loaId']),
    jobsCreated: pickFirst(ocrData, [
      'jobsCreated',
      'borrowerinformation_jobs_created',
    ]),
    jobsRetained: pickFirst(ocrData, [
      'jobsRetained',
      'borrowerinformation_jobs_retained',
    ]),
    lenderName: pickFirst(ocrData, [
      'lenderInformation_lenderName',
      'lenderinformation_participatinglender',
      'financialInstitution_name',
    ]),
    lenderContactName: pickFirst(ocrData, [
      'lenderInformation_contactName',
      'lenderinformation_contact_name',
      'calcaplendercertification_prited_name_authorized_signer',
      'financialInstitution_officerContactPerson',
    ]),
    lenderEmail: pickFirst(ocrData, [
      'lenderInformation_email',
      'lenderinformation_contact_email',
      'financialInstitution_emailAddress',
      'lenderInformation_emailAddress',
    ]),
    lenderPhoneNumber: pickFirst(ocrData, [
      'lenderInformation_phoneNumber',
      'lenderinformation_contact_phone_number',
      'lendinginformation_contact_phone_number',
      'financialInstitution_phoneNumbers',
    ]),

  };
};

/**
 * Persisted `formData` stores merged Yes/No selects as separate API keys (`..._yes` / `..._no` booleans).
 * Read-only OCR UI uses merged field names. Reconstruct merged keys so selects show the right value.
 */
export const hydrateFormattedOcrDataForMergedPairedFields = (
  flatData,
  parsedOcrData
) => {
  const out = { ...(flatData || {}) };
  const truthy = (v) =>
    v === true ||
    v === 'true' ||
    v === 'Yes' ||
    v === 'yes' ||
    v === 1 ||
    v === '1';
  const walk = (fields) => {
    if (!Array.isArray(fields)) return;
    fields.forEach((field) => {
      const mergedName = field?.name ?? field?.key;
      const pair = field?._ocrYesNoPairKeys;
      if (!pair || !mergedName) return;
      const { yesKey, noKey } = pair;
      const y = truthy(out[yesKey]);
      const n = truthy(out[noKey]);
      if (y && !n) out[mergedName] = 'Yes';
      else if (n && !y) out[mergedName] = 'No';
      else if (y && n) out[mergedName] = 'Yes';
      else if (out[mergedName] === undefined) out[mergedName] = null;
    });
  };
  Object.values(parsedOcrData || {}).forEach(walk);
  // Legacy MISSING/RECEIVED on borrower/authorized signatures → blank for Yes/No select.
  Object.keys(out).forEach((key) => {
    if (!isOcrAnalystSignatureConfirmField(key)) return;
    out[key] = normalizeSignatureConfirmSelectValue(out[key]) ?? '';
  });
  return out;
};

export const formatOcrDataForSubmit = () => {
  const { parsedOcrData } = ocrSignal.value;
  const formattedOcrData = {};

  Object.keys(parsedOcrData).forEach((category) => {
    (parsedOcrData[category] ?? []).forEach((field) => {
      const key = field.name ?? field.key;
      if (field._ocrYesNoPairKeys) {
        const { yesKey, noKey } = field._ocrYesNoPairKeys;
        const v = field.value;
        const isYes = v === 'Yes' || v === 'yes';
        const isNo = v === 'No' || v === 'no';
        formattedOcrData[yesKey] = Boolean(isYes);
        formattedOcrData[noKey] = Boolean(isNo);
        return;
      }
      if (key.includes('email')) {
        formattedOcrData[key] = field?.value?.toLowerCase?.()?.replace(/\s/g, '');
        return;
      }
      if (field.type === 'table') {
        const headers = field?.value?.headers ?? [];
        formattedOcrData[key] = (field?.value?.rows ?? []).map((tableRow) => {
          const trimmedRowObj = {};
          headers.forEach((h) => {
            let cellValue = tableRow[h];
            if (h.includes('requiredDocuments')) cellValue = cellValue ? true : false;
            trimmedRowObj[h] = cellValue;
          });
          return trimmedRowObj;
        });
        return;
      }
      formattedOcrData[key] = field.value;
    });
  });

  const normalizedOcrData = normalizeOcrData(formattedOcrData);
  ocrSignal.update({ formattedOcrData, normalizedOcrData });
};

export const displayHeader = (header) => {
  if (
    header === 'Item' ||
    header === 'Value' ||
    header.includes('COLUMN') ||
    header === 'Provided'
  ) {
    return false;
  }

  return true;
};

export const columnIsInput = (header) => {
  if (
    header === 'Item' ||
    header === 'Maximum Collateral Support Available' ||
    header === 'Documents' ||
    header === 'Investment Cost'
  ) {
    return false;
  }
  return true;
};

export const getCustomLabel = (fieldName) => {
  let label = '';

  switch (fieldName) {
    case 'borrowerCertificationDisadvantageIndividuals_membershipOfGroup':
    case 'ssbciBorrowerCertificationBusinessEnterprises_membershipOfAGroupThatHasBeenSubjectedToRacialOrEthnicPrejudice':
    case 'ssbciBorrowerRelatedToBusinessOwned_membershipOfGroup':
      label =
        'membership of a group that has been subjected to racial or ethnic prejudice or cultural bias within American society';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_gender':
    case 'ssbciBorrowerCertificationBusinessEnterprises_gender':
    case 'ssbciBorrowerRelatedToBusinessOwned_gender':
      label = 'gender';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_veteran':
    case 'ssbciBorrowerCertificationBusinessEnterprises_veteranStatus':
    case 'ssbciBorrowerRelatedToBusinessOwned_veteran':
      label = 'veteran status';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_limitedEnglish':
    case 'ssbciBorrowerCertificationBusinessEnterprises_limitedEnglishProficiency':
    case 'ssbciBorrowerRelatedToBusinessOwned_limitedEnglish':
      label = 'limited English proficiency';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_disability':
    case 'ssbciBorrowerCertificationBusinessEnterprises_disability':
    case 'ssbciBorrowerRelatedToBusinessOwned_disability':
      label = 'disability';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_longtermResidence':
    case 'ssbciBorrowerCertificationBusinessEnterprises_longTermResidenceInAnEnvironmentIsolatedFromMainstreamAmericanSociety':
    case 'ssbciBorrowerRelatedToBusinessOwned_longtermResidencyIsolated':
      label =
        'long-term residence in an environment isolated from the mainstream of American society';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_membershipOfAFederallyOrStateRecognizedIndianTribe':
    case 'ssbciBorrowerCertificationBusinessEnterprises_membershipOfAFederallyOrStateRecognizedIndianTribe':
    case 'ssbciBorrowerRelatedToBusinessOwned_membershipFederallyIndianTribe':
      label = 'membership of a federally or state-recognized Indian Tribe';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_longtermResidenceInARuralCommunity':
    case 'ssbciBorrowerCertificationBusinessEnterprises_longTermResidenceInARuralCommunity':
    case 'ssbciBorrowerRelatedToBusinessOwned_longTermRuralCommunity':
      label = 'long-term residence in a rural community';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_residenceUS':
    case 'ssbciBorrowerCertificationBusinessEnterprises_residenceInAUSTerritory':
    case 'ssbciBorrowerRelatedToBusinessOwned_residencyInUsTerritory':
      label = 'residence in a U.S. territory';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_residenceInACommunityUndergoingEconomicTransition':
    case 'ssbciBorrowerCertificationBusinessEnterprises_residenceInACommunityUndergoingEconomicTransitions':
    case 'ssbciBorrowerRelatedToBusinessOwned_residencecommunityEconomic':
      label =
        'residence in a community undergoing economic transitions (including communities impacted by the shift towards a net-zero economy or deindustrialization)';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_membershipOfAnotherUnderservedCommunity':
    case 'ssbciBorrowerCertificationBusinessEnterprises_membershipOfAnotherUnderservedCommunity':
    case 'ssbciBorrowerRelatedToBusinessOwned_membershipUnderservedCommunity':
      label = 'membership of another underserved community';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_businessOwnedAreasCFR':
    case 'ssbciBorrowerCertificationBusinessEnterprises_businessEnterpriseThatIsOwnedAndControlledByIndividualsInCDFIAreas':
    case 'ssbciBorrowerRelatedToBusinessOwned_businessOwnedByIndividualsCDFIAreas':
      label =
        '2. Business enterprise that is owned and controlled by individuals whose residences are in CDFI Investment Areas, as defined in 12 C.F.R. ';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_businessOwnedAreasIndividualAddress':
    case 'ssbciBorrowerCertificationBusinessEnterprises_individualAddress':
    case 'ssbciBorrowerRelatedToBusinessOwned_individualAddressCDFIInvestment':
      label = "Individual(s)' Address(es) in CDFI Investment Areas:";
      break;
    case 'borrowerCertificationDisadvantageIndividuals_businessEnterprisesThatWillBuildCDFI':
    case 'ssbciBorrowerCertificationBusinessEnterprises_businessEnterpriseThatWillBuildOpenOrOperateALocationInACDFIArea':
    case 'ssbciBorrowerRelatedToBusinessOwned_businessEnterpriseOperateCDFIArea':
      label =
        '3. Business enterprise that will build, open, or operate a location in a CDFI Investment Area, as defined in 12 C.F.R.';
      break;
    case 'borrowerCertificationDisadvantageIndividuals_businessAddressInCdfiArea':
    case 'ssbciBorrowerCertificationBusinessEnterprises_businessAddressInCDFIArea':
    case 'ssbciBorrowerRelatedToBusinessOwned_businessAddressInCDFIArea':
      label = 'Business Address In CDFI Area';
      break;
    case 'borrowerInformation_monthYearStablished':
      label = 'Month/Year Established';
      break;
    case 'lenderinformation_participatinglender':
      label = 'Participating Lender';
      break;
    case 'lenderinformation_calpap_id':
      label = 'Lender CalCAP ID#';
      break;
    case 'lenderinformation_contact_name':
      label = 'Lender Contact Name';
      break;
    case 'lenderinformation_contact_email':
      label = 'Lender Contact Email';
      break;
    case 'lendinginformation_contact_phone_number':
      label = 'Lender Contact Phone Number';
      break;
    case 'collateral_support_info_supported percentage_request':
      label = 'Supported Percentage Request';
      break;
    case 'borrowerinformation_sac_reason':
      label = 'SAC Reason';
      break;
    case 'borrowerinformation_in_sac_or_sedi':
      label = 'In SAC or SEDI';
      break;
    case 'ccapsediownedbusiness_question_1':
    case 'ccapSEDIOwnedBusiness_question_1':
      label =
        'Business enterprise that is owned and controlled by individuals who have had their access to credit on reasonable terms diminished as compared to others in comparable economic circumstances, due to their:';
      break;
    case 'ccapsediownedbusiness_question2':
    case 'ccapSEDIOwnedBusiness_question2':
      label =
        'Business enterprise that is owned and controlled by individuals whose residences are in CDFI Investment Areas, as defined in 12 C.F.R. § 1805.201(b)(3)(ii).iv https://home.treasury.gov/policy-issues/small-business-programs/state-small-business-credit-initiative-ssbci/2021-ssbci/cdfi-fund-investment-areas (“IAQualified” indicates “Yes”, the address is located in a qualified CDFI Investment Area. If the “IAQualified” field is equal to “No”, the address is not located in a qualified CDFI Investment Area.)';
      break;
    case 'ccapsediownedbusiness_question3':
    case 'ccapSEDIOwnedBusiness_question3':
      label =
        'Business enterprise that will build, open, or operate a location in a CDFI Investment Area, as defined in 12 C.F.R. § 1805.201(b)(3)(ii). https://home.treasury.gov/policy-issues/small-business-programs/state-small-business-credit-initiative-ssbci/2021-ssbci/cdfi-fund-investment-areas (“IAQualified” indicates “Yes”, the address is located in a qualified CDFI Investment Area. If the “IAQualified” field is equal to “No”, the address is not located in a qualified CDFI Investment Area.)';
      break;
    case 'calcap_sb_csp_questionaire_1_sec2_if_yes_institution_name':
      label = 'Institution Name (if refinance at another institution)';
      break;
    case 'box3_calcap_loan_number':
    case 'box3_calpap_loan_number':
    case 'calcaplendercertification_lender_loan_number':
      label = 'CalCAP Loan Number';
      break;
    case 'page1_calcapuseonly_date_received':
    case 'calcapuseonly_date_received':
      label = 'Date Received';
      break;
    case 'page1_calcapuseonly_csp_number':
    case 'calcapuseonly_csp_number':
      label = 'CSP Number';
      break;
    case 'page1_calcapuseonly_application_number':
    case 'calcapuseonly_application_number':
      label = 'Application Number';
      break;
    case 'page1_calcap_use_only_loan_number':
    case 'calcap_use_only_loan_number':
      label = 'CalCAP Loan Number';
      break;
    default:
      break;
  }

  return label;
};

export const renderAdditionalText = (fieldName) => {
  switch (fieldName) {
    case 'borrowerCertificationDisadvantageIndividuals_membershipOfGroup':
    case 'ssbciBorrowerCertificationBusinessEnterprises_membershipOfAGroupThatHasBeenSubjectedToRacialOrEthnicPrejudice':
    case 'ssbciBorrowerRelatedToBusinessOwned_membershipOfGroup':
      return (
        <>
          <p className="mt-16">
            The borrower hereby certifies to the lender that it is a:
          </p>
          <p>
            1. Business enterprise that is owned and controlled by individuals
            who have had their access to credit on reasonable terms diminished
            as compared to others in comparable economic circumstances, due to
            their:
          </p>
        </>
      );
    case 'borrowerCertificationDisadvantageIndividuals_businessOwnedAreasCFR':
    case 'ssbciBorrowerCertificationBusinessEnterprises_businessEnterpriseThatIsOwnedAndControlledByIndividualsInCDFIAreas':
    case 'ssbciBorrowerRelatedToBusinessOwned_businessOwnedByIndividualsCDFIAreas':
    case 'borrowerCertificationDisadvantageIndividuals_businessEnterprisesThatWillBuildCDFI':
    case 'ssbciBorrowerCertificationBusinessEnterprises_businessEnterpriseThatWillBuildOpenOrOperateALocationInACDFIArea':
    case 'ssbciBorrowerRelatedToBusinessOwned_businessEnterpriseOperateCDFIArea':
    case 'borrowerCertificationDisadvantageIndividuals_signature':
    case 'ssbciBorrowerCertificationBusinessEnterprises_signature':
    case 'ssbciBorrowerRelatedToBusinessOwned_signature':
      return <hr />;
    default:
      return null;
  }
};

/**
 * Maps parsed OCR category names to form section titles aligned with the CalCAP SB packet (e.g. 0235-00103_Sigrune Chapman_SB.pdf).
 */
export const getCustomCategory = (category) => {
  const raw =
    category == null || typeof category !== 'string'
      ? ''
      : category.trim();
  if (/^calcapuseonly$/i.test(raw)) {
    return 'CalCAP Use Only';
  }

  const key = raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : category;
  let customCategory = category;

  switch (key) {
    case 'Generalinfo':
    case 'Generalnfo':
      customCategory = 'General Information';
      break;
    case ' Section 1':
    case 'Section 1':
      customCategory = 'Application Checklist';
      break;
    case 'Borrowerinformation':
    case 'Borroweinformation':
      customCategory = 'Borrower Information';
      break;
    case 'Loaninformation':
    case 'Loaininformation':
      customCategory = 'Loan Information';
      break;
    case 'Lenderinformation':
    case 'Lendinginformation':
      customCategory = 'Lender Information';
      break;
    case 'Calcaplendercertification':
    case 'Lendercertification':
      customCategory = 'Lender Certification';
      break;
    case 'Loancertification':
      customCategory = 'Loan Certification';
      break;
    case 'Borrowercertificationforsmallbusinessloanenrollments':
      customCategory = 'Borrower Certification for Small Business Loan Enrollments';
      break;
    case 'Calpapborroweragreement':
      customCategory = 'Borrower Agreement';
      break;
    case 'Calpapborrowercertification':
      customCategory = 'Borrower Certification';
      break;
    case 'Calcapuseonly':
      customCategory = 'CalCAP Use Only';
      break;
    case 'Calcapborrowercertification':
      customCategory = 'CalCAP Borrower Certification';
      break;
    case 'Calcaplendercertification':
      customCategory = 'CalCAP Lender Certification';
      break;
    case 'Calcaploancertification':
      customCategory = 'CalCAP Loan Certification';
      break;
    case 'Calcapborrowercertification':
      customCategory = 'CalCAP Borrower Certification';
      break;
    case 'Calcapborroweragreement':
      customCategory = 'CalCAP Borrower Agreement';
      break;
    case 'Ccapsediownedbusiness':
      customCategory = 'CalCAP SEDI-Owned Business';
      break;
    case 'Borrowerdemographics':
      customCategory = 'Demographics';
      break;
    case 'Supplumentalsaccertification':
    case 'Supplementalsaccertification':
      customCategory = 'Supplemental SAC Certification';
      break;
    case 'Collateral':
      customCategory = 'CalCAP Collateral Support or CalCAP Small Business';
      break;
    case 'Calpapcertification':
      customCategory = 'CalCAP Certification';
      break;
    case 'Calcap':
      customCategory = 'Commercial Real Estate and Refinancing Lender Questionnaire';
      break;
    case 'Calcapsbcspquestionaire':
    case 'Calcapsbcspquestionnaire':
      customCategory = 'CalCAP SB CSP Questionnaire';
      break;
    case 'Box1':
    case 'Box2':
    case 'Box3':
      customCategory = category.replace(/^Box(\d)/i, 'Box $1');
      break;
    default:
      customCategory = key ? key.charAt(0).toUpperCase() + key.slice(1) : category;
      break;
  }

  return customCategory;
}

export const formatOcrLabel = (label) => {
  if (label == null || typeof label !== 'string') return label;
  return label
    .replace(/\bEin\b/gi, 'EIN')
    .replace(/\bNaics\b/gi, 'NAICS')
    .replace(/\bCCAP\b/g, 'CalCAP')
    .replace(/\bCcap\b/g, 'CalCAP')
    .replace(/\bsac\b/gi, 'SAC')
    .replace(/\bCsp\b/gi, 'CSP')
    .replace(/\bCalcap\b/gi, 'CalCAP')
    .replace(/\bCalpap\b/gi, 'CalCAP')
    .replace(/\bCalcapcollateralsupport\b/gi, 'CalCAP Collateral Support')
    .replace(/\bCalcapsmallbusiness\b/gi, 'CalCAP Small Business')
    .replace(/\bRealestate\b/gi, 'Real Estate')
    .replace(/\bSexualorientation\b/gi, 'Sexual Orientation')
    .replace(/\bCdfi\b/gi, 'CDFI')
    .replace(/\Sedi\b/gi, 'SEDI')
    .replace(/\Fte\b/gi, 'FTE')
    .replace(/\bBusinessname\b/gi, 'Business Name')
    .replace(/\bBuiness\b/gi, 'Business')
    .replace(/\bUndeserved\b/gi, 'Underserved')
    .replace(/\bLoan Apr\b/g, 'Loan APR')
    .replace(/\bQuestionaire\b/gi, 'Questionnaire');
};

export const shouldFormatAsCurrency = (fieldName, label, header) => {
  if (fieldName && OCR_CURRENCY_FIELD_NAMES.has(fieldName)) {
    return true;
  }

  const textToCheck = `${fieldName || ''} ${label || ''} ${header || ''}`.toLowerCase();

  const currencyKeywords = [
    'amount', 'cost', 'price', 'dollar', 'currency', 'money',
    'payment', 'loan amount', 'principal', 'fee', 'charge',
    'disbursement', 'reimbursement', 'allocation', 'funding',
    'investment', 'collateral', 'total amount', 'balance',
    'revenue', 'revenues', 'income', 'sales', 'expense', 'expenses',
    'expenditure', 'expenditures', 'profit', 'loss', 'earnings'
  ];

  if (!textToCheck.includes('interest rate') && textToCheck.includes('interest')) {
    currencyKeywords.push('interest');
  }

  const hasCurrencyKeyword = currencyKeywords.some(keyword => textToCheck.includes(keyword));

  if (hasCurrencyKeyword) {
    if (
      textToCheck.includes('date') ||
      (textToCheck.includes('year') && !textToCheck.includes('revenue') && !textToCheck.includes('fiscal') && !textToCheck.includes('annual')) ||
      (textToCheck.includes('month') && !textToCheck.includes('revenue') && !textToCheck.includes('fiscal') && !textToCheck.includes('annual')) ||
      (textToCheck.includes('day') && !textToCheck.includes('revenue') && !textToCheck.includes('fiscal') && !textToCheck.includes('annual'))
    ) {
      return false;
    }
    return true;
  }

  if (
    textToCheck.includes('date') ||
    textToCheck.includes('year') ||
    textToCheck.includes('month') ||
    textToCheck.includes('day') ||
    textToCheck.includes('phone') ||
    textToCheck.includes('ein') ||
    textToCheck.includes('zip') ||
    (textToCheck.includes('number') && !textToCheck.includes('loan number') && !textToCheck.includes('account number'))
  ) {
    return false;
  }

  return false;
};

export const shouldFormatAsPercent = (fieldName, label, header) => {
  if (fieldName && OCR_PERCENT_FIELD_NAMES.has(fieldName)) {
    return true;
  }

  const textToCheck = `${fieldName || ''} ${label || ''} ${header || ''}`.toLowerCase();

  const percentKeywords = [
    'percent', 'percentage', 'guaranty percentage', 'guarantee percentage',
    'leverage', 'ltv', 'ltc', 'lti', 'rate', 'interest rate'
  ];
  const hasPercentKeyword = percentKeywords.some(keyword => textToCheck.includes(keyword));

  if (hasPercentKeyword) {
    if (
      textToCheck.includes('date') ||
      textToCheck.includes('year') ||
      textToCheck.includes('month') ||
      textToCheck.includes('day')
    ) {
      return false;
    }
    return true;
  }

  if (
    textToCheck.includes('date') ||
    textToCheck.includes('year') ||
    textToCheck.includes('month') ||
    textToCheck.includes('day') ||
    textToCheck.includes('phone') ||
    textToCheck.includes('ein') ||
    textToCheck.includes('zip') ||
    (textToCheck.includes('number') && !textToCheck.includes('loan number') && !textToCheck.includes('account number'))
  ) {
    return false;
  }

  return false;
};

export const formatOcrCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'string' && value.includes('$')) {
    return value;
  }

  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : Number(value);

  if (isNaN(numValue)) {
    return value;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

export const formatOcrPercent = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  let numValue;
  if (typeof value === 'string' && value.includes('%')) {
    numValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
  } else {
    numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : Number(value);
  }

  if (isNaN(numValue)) {
    return value;
  }

  return `${numValue.toFixed(2)}%`;
};

export const formatOcrValue = (value, fieldName, label, header) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const datePattern = /(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})|(\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2})/;
    if (datePattern.test(value)) {
      return value;
    }
    if (/[a-zA-Z]/.test(value.replace(/[$%]/g, ''))) {
      return value;
    }
  }

  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : Number(value);
  const isNumeric = !isNaN(numValue);

  if (!isNumeric) {
    return value;
  }

  if (shouldFormatAsPercent(fieldName, label, header)) {
    return formatOcrPercent(value);
  }

  if (shouldFormatAsCurrency(fieldName, label, header)) {
    return formatOcrCurrency(value);
  }

  return value;
};
